// CHUNK 1 – LOGIC + STATES + FULL CALENDAR SUPPORT
import React, { useState, useEffect, useMemo } from "react";
import DashboardMenu from "./DashboardMenu";
import { useNavigate } from "react-router-dom";
import { GetHomeDataService } from "../../services/HomeDataServices";
import { GetActivityService } from "../../services/ActivityServices";

import { PiSuitcaseSimple } from "react-icons/pi";
import { TbBriefcaseOff, TbDeviceAirpodsCase, TbMoneybag } from "react-icons/tb";
import {
  MdOutlineDriveFileMove,
  MdOutlineDriveFileMoveRtl,
  MdOutlinePayment,
} from "react-icons/md";
import { FiFileText } from "react-icons/fi";
import { RiBillLine } from "react-icons/ri";
import { LuCalendarClock } from "react-icons/lu";
import { AiOutlineAlert } from "react-icons/ai";
import { IoCalendarClearOutline } from "react-icons/io5";

import ReactApexChart from "react-apexcharts";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from "moment";

function Dashboard() {
  const navigator = useNavigate();

  // ----------------------------------------
  // MAIN DASHBOARD DATA STATE
  // ----------------------------------------
  const [data, setdata] = useState({
    total_case: 0,
    total_case_pending: 0,
    total_case_progress: 0,
    total_case_completed: 0,
    total_case_hold: 0,

    total_opinion_file: 0,
    total_opinion_file_pending: 0,
    total_opinion_file_progress: 0,
    total_opinion_file_completed: 0,
    total_opinion_file_hold: 0,

    bills: [],
    billamount: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
    payment: [],
    paymentamount: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
    reminder: [],
    reminder_active: [],
    today_case: [],
    today_office_file: [],
    today_case_total: 0,
    today_bills: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
    today_payment: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
    today_reminder: [],
    today_reminder_total: 0,
  });

  // ----------------------------------------
  // HEARING LIST STATE (from activity service)
  // ----------------------------------------
  const [hearingList, setHearingList] = useState([]);

  // ----------------------------------------
  // WEEKLY CALENDAR STATES
  // ----------------------------------------
  const [selectedDate, setSelectedDate] = useState(moment().startOf("day"));
  const [weekDates, setWeekDates] = useState([]);
  const [filteredHearings, setFilteredHearings] = useState([]);

  // ----------------------------------------
  // FULL-CALENDAR MODAL STATES
  // ----------------------------------------
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date()); // controls month in modal

  // Map of YYYY-MM-DD -> hearings[] (for dots on calendar)
  const hearingDatesMap = useMemo(() => {
    const map = {};
    hearingList.forEach((h) => {
      if (!h?.next_hearing_date) return;
      const key = moment(h.next_hearing_date).format("YYYY-MM-DD");
      if (!map[key]) map[key] = [];
      map[key].push(h);
    });
    return map;
  }, [hearingList]);

  const hasHearingOn = (date) => {
    const key = moment(date).format("YYYY-MM-DD");
    return !!hearingDatesMap[key];
  };

  // ----------------------------------------
  // GENERATE WEEK (Sun → Sat)
  // ----------------------------------------
  function generateWeek(date) {
    const startOfWeek = moment(date).startOf("week"); // Sunday
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(moment(startOfWeek).add(i, "days"));
    }
    setWeekDates(week);
  }

  // ----------------------------------------
  // WEEK NAVIGATION
  // ----------------------------------------
  function goPrevWeek() {
    const newDate = moment(selectedDate).subtract(7, "days");
    setSelectedDate(newDate);
    generateWeek(newDate);
  }

  function goNextWeek() {
    const newDate = moment(selectedDate).add(7, "days");
    setSelectedDate(newDate);
    generateWeek(newDate);
  }

  // ----------------------------------------
  // FILTER HEARINGS WHEN SELECTED DATE CHANGES
  // ----------------------------------------
  useEffect(() => {
    const filtered = hearingList.filter((h) =>
      moment(h.next_hearing_date).isSame(selectedDate, "day")
    );
    setFilteredHearings(filtered);
  }, [selectedDate, hearingList]);

  // ----------------------------------------
  // FETCH UPCOMING HEARINGS FROM ACTIVITY SERVICE
  // ----------------------------------------
  async function getUpcomingHearings() {
    try {
      const res = await GetActivityService("case");
      const list = res?.data?.datas || [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const upcoming = list.filter(
        (a) =>
          a?.next_hearing_date && new Date(a.next_hearing_date) >= today
      );

      upcoming.sort(
        (a, b) => new Date(a.next_hearing_date) - new Date(b.next_hearing_date)
      );

      setHearingList(upcoming);
    } catch (err) {
      console.log("Error fetching hearings", err);
    }
  }

  // ----------------------------------------
  // FETCH DASHBOARD DATA
  // ----------------------------------------
  async function getData() {
    const response = await GetHomeDataService();
    const d = response?.data?.data || {};

    const arr = [
      { name: "Total", data: [d?.total_case, d?.total_opinion_file] },
      {
        name: "Completed",
        data: [d?.total_case_completed, d?.total_opinion_file_completed],
      },
      {
        name: "Pending",
        data: [d?.total_case_pending, d?.total_opinion_file_pending],
      },
      {
        name: "Progress",
        data: [d?.total_case_progress, d?.total_opinion_file_progress],
      },
      {
        name: "Hold",
        data: [d?.total_case_hold, d?.total_opinion_file_hold],
      },
    ];

    setchartOptions1((prev) => ({
      ...prev,
      series: arr,
      xaxis: { categories: ["Cases", "Opinion File"] },
    }));

    setchartOptions2((prev) => ({
      ...prev,
      series: [
        {
          name: "Amount",
          data: [
            d?.today_bills?.totalAmountWithGst || 0,
            d?.today_payment?.totalAmountWithGst || 0,
          ],
        },
      ],
    }));

    setdata(d);
  }

  // ----------------------------------------
  // CHART STATES (BAR CHARTS)
  // ----------------------------------------
  const [chartOptions1, setchartOptions1] = useState({
    series: [],
    chart: { toolbar: { show: false } },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
      },
    },
    dataLabels: { enabled: false },
    grid: { show: true, borderColor: "#fafafa" },
    stroke: { width: 1, curve: "smooth", colors: ["transparent"] },
    xaxis: { categories: [] },
    yaxis: { title: { text: "Count" } },
    fill: { opacity: 1 },
    tooltip: {
      y: {
        formatter: function (val) {
          return val;
        },
      },
    },
    fill: ["#027cd1"],
  });

  const [chartOptions2, setchartOptions2] = useState({
    series: [],
    chart: { toolbar: { show: false } },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
      },
    },
    dataLabels: { enabled: false },
    grid: { show: true, borderColor: "#fafafa" },
    stroke: { width: 1, curve: "smooth", colors: ["transparent"] },
    xaxis: { categories: ["Bill Amount", "Payment Collection"] },
    yaxis: { title: { text: "Count" } },
    fill: { opacity: 1 },
    tooltip: {
      y: {
        formatter: function (val) {
          return val;
        },
      },
    },
    fill: ["#027cd1"],
  });

  // ----------------------------------------
  // RUN ON MOUNT
  // ----------------------------------------
  useEffect(() => {
    getData();
    getUpcomingHearings();
    generateWeek(moment());
  }, []);

  // ----------------------------------------
  // FORMATTER
  // ----------------------------------------
  let rupeeIndian = Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  });

  // ----------------------------------------
  // RADIAL CHART OPTIONS + REVENUE %
  // ----------------------------------------
  const chartOptions = {
    plotOptions: {
      color: ["#000", "#000"],
      size: "40%",
      radialBar: {
        inverseOrder: false,
        startAngle: 0,
        endAngle: 360,
        offsetX: 0,
        offsetY: 0,
        hollow: {
          margin: 5,
          size: "50%",
          color: "#f2f2f2",
          image: undefined,
          imageWidth: 150,
          imageHeight: 150,
          imageOffsetX: 0,
          imageOffsetY: 0,
          imageClipped: true,
          position: "front",
        },
        track: {
          show: true,
          background: "#f2f2f2",
          strokeWidth: "50%",
          opacity: 1,
          margin: 5,
          dropShadow: {
            enabled: false,
            top: 0,
            left: 0,
            opacity: 1,
          },
        },
        dataLabels: {
          show: true,
          name: {
            show: true,
            fontSize: "12px",
            fontWeight: 400,
            color: "#000",
          },
          value: {
            show: true,
            fontSize: "14px",
            fontWeight: 900,
            offsetY: 1,
            formatter: function (val) {
              return val + "%";
            },
          },
        },
      },
    },
    labels: ["Revenue Forecast"],
    fill: {
      colors: ["#008ef2"],
    },
  };

  const revenuePercent =
    data?.billamount?.totalAmountWithGst > 0
      ? Math.floor(
          (data?.paymentamount?.totalAmountWithGst /
            data?.billamount?.totalAmountWithGst) *
            100
        )
      : 0;

  // ----------------------------------------
  // FULL-CALENDAR HELPERS (MODAL)
  // ----------------------------------------
  const openCalendar = () => setIsCalendarOpen(true);
  const closeCalendar = () => setIsCalendarOpen(false);

  const goPrevMonth = () => {
    setCalendarDate((prev) => moment(prev).subtract(1, "month").toDate());
  };

  const goNextMonth = () => {
    setCalendarDate((prev) => moment(prev).add(1, "month").toDate());
  };

  const handleCalendarChange = (date) => {
    // When user picks a date in full calendar, sync weekly strip + list
    const m = moment(date).startOf("day");
    setCalendarDate(date);
    setSelectedDate(m);
    generateWeek(m);
    setIsCalendarOpen(false);
  };

  // Dots under days that have hearings
  const renderTileContent = ({ date, view }) => {
    if (view !== "month") return null;
    if (!hasHearingOn(date)) return null;

    return (
      <div className="flex justify-center mt-1">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      </div>
    );
  };

  const calendarMonthLabel = moment(calendarDate).format("MMMM YYYY");

  // ---- JSX starts in CHUNK 2 ----
// CHUNK 2 — JSX (UI + MODAL + CALENDAR + WEEK STRIP)

return (
  <div className="md:h-screen md:min-h-screen md:max-h-screen md:overflow-hidden">
    <div className="md:flex">
      <DashboardMenu />

      <div className="md:w-[100%] md:w-[86%] flex md:h-screen md:min-h-screen md:max-h-screen md:overflow-y-scroll">
        <div className='w-[100%] px-4 pt-5 '>
           
           {/* section 1  */}
           <div className='grid items-center grid-cols-2 border-t border-b border-l border-r md:grid-cols-6 justify-evenly border-slate-100'>
                  <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><PiSuitcaseSimple size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> Total Case </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case))?.split('.')[0].slice(1)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbDeviceAirpodsCase size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> Active Case </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case_pending + data?.total_case_progress))?.split('.')[0].slice(1)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbBriefcaseOff size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> InActive Case </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case_hold))?.split('.')[0].slice(1)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlineDriveFileMove size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> Total Opinion File </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file))?.split('.')[0].slice(1)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><FiFileText size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> Active Opinion File </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file_pending + data?.total_opinion_file_progress))?.split('.')[0].slice(1)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlineDriveFileMoveRtl size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> InActive Opinion File </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file_hold))?.split('.')[0].slice(1)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-r border-slate-100' onClick={()=>navigator('/bills/list')}>
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><RiBillLine size={23} className='p-1 mr-2 text-purple-500 bg-purple-200' /> Total Bills </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.bills))?.split('.')[0].slice(1)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-r border-slate-100' >
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlinePayment size={23} className='p-1 mr-2 text-purple-500 bg-purple-200' /> Total Payment </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.payment))?.split('.')[0].slice(1)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-r border-slate-100' >
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><AiOutlineAlert size={23} className='p-1 mr-2 text-red-500 bg-red-200' /> Total Reminder </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.reminder))?.split('.')[0].slice(1)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-r border-slate-100' >
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><LuCalendarClock size={23} className='p-1 mr-2 text-red-500 bg-red-200' /> Active Reminder </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.reminder_active))?.split('.')[0].slice(1)}</h6>
                  </div>

                  <div className='w-full px-2 py-2 border-r border-slate-100'>
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbMoneybag size={23} className='p-1 mr-2 text-green-500 bg-green-200' /> Total Revenue </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.billamount?.totalAmountWithGst))?.split('.')[0].slice(0)}</h6>
                  </div>
                  <div className='w-full px-2 py-2 border-r border-slate-100' >
                    <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbMoneybag size={23} className='p-1 mr-2 text-green-500 bg-green-200' /> Collect Revenue </h6>
                    <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.paymentamount?.totalAmountWithGst))?.split('.')[0].slice(0)}</h6>
                  </div>
           </div>  

           {/* section 2  */}
           <div className='mt-5 md:flex md:mt-2'>
              <div className=' md:w-[29.5%] md:mr-[0.5%] border border-slate-100 p-5 px-3 py-2'>

              <h6 className='text-[13px] font-[700]'>Revenue Generated</h6>

              <div className='flex '>
                <div className='-mt-5 -ml-14'>
                <ReactApexChart options={chartOptions} size={'10'} series={
                  [Math.floor((data?.paymentamount?.totalAmountWithGst/data?.billamount?.totalAmountWithGst)*100)]} type="radialBar" width={240} height={240} />
                </div>
                <div className='w-[100%] -ml-[40px] -mt-8 -ml-2'>

                <div className='relative w-full mt-16'>
                  <h6 className='text-[11px] font-[600]'>Bill Amount </h6>
                    <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-2 dark:bg-gray-700">
                      <div className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none w-[100%]`} style={{width:data?.billamount?.totalAmountWithGst > 0 ? '100%' : '0%'}}  >{data?.billamount?.totalAmountWithGst > 0 ? '100%' : '0%'}</div>
                    </div>
                    <h6 className='text-[11px] font-[700] absolute top-0 right-0'>{rupeeIndian?.format(JSON.stringify(data?.billamount?.totalAmountWithGst))?.split('.')[0]}</h6>

                </div>

                <div className='relative w-full mt-4 ml-0'>
                  <h6 className='text-[11px] font-[600]'>Payment Collected </h6>
                    <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-4 dark:bg-gray-700">
                      <div className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none `} style={{width: data?.paymentamount?.totalAmountWithGst  >= data?.billamount?.totalAmountWithGst ? '100%' :  `${Math.round((data?.paymentamount?.totalAmountWithGst/data?.billamount?.totalAmountWithGst))*100}%`}} >{data?.paymentamount?.totalAmountWithGst > 0 ?  Math.round((data?.paymentamount?.totalAmountWithGst / data?.billamount?.totalAmountWithGst)*100): 0}%</div>
                    </div>
                    <h6 className='text-[11px] font-[700] absolute top-0 right-0'>{rupeeIndian?.format(JSON.stringify(data?.paymentamount?.totalAmountWithGst))?.split('.')[0]}</h6>

                </div>
              
                </div>
              </div>

              </div>

              <div className={`border mt-2 md:mt-0 md:w-[69.5%] md:ml-[0.5%]   border-slate-100 px-3 py-2 relative`}>
                    <h1 className='text-[13px] font-[700]'>Case / Opinion File</h1>
                    <ReactApexChart options={chartOptions1} size={'10'} series={chartOptions1.series} type="bar" width={'100%'}  height={220} />
              </div>
           </div>

           {/* section 3  */}
           <div className='mt-5 md:flex md:mt-2'>

           <div className='border md:w-[33%] border-slate-100 p-2'>
              <h6 className='text-[13px] font-[700]'>Today Case / Opinion File Register ({data?.today_case_total})</h6>

                {data?.today_case_total === 0 &&
                 <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
                 <IoCalendarClearOutline size={40} color="#ddd" />
                 <h6 className='text-[11px] mt-2'>No Case / Opinion File found for today</h6>
                 <h6 className='text-[11px] text-center mt-1'>We found that you have not added any case or opinion file for today session.</h6>
                 </div>
                }
              
                {(data?.today_case?.length > 0 || data?.today_office_file?.length > 0) &&
                <div className='overflow-y-scroll min-h-[85%] max-h-[85%]'>
                  <div className='flex text-[11px] border mt-2'>
                    <h6 className='w-[25%] p-1 border-r'>Client</h6>
                    <h6 className='w-[25%] p-1 border-r'>Office File No / Case No</h6>
                    <h6 className='w-[25%] p-1 border-r'>Case Work</h6>
                    <h6 className='w-[25%] p-1 '>Status</h6>
                  </div>

                  {data?.today_case?.map((c,i)=>(
                    <div key={i} onClick={()=>navigator('/case/civil_court/detail',{state:c})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
                    <h6 className='w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r'>{c?.client?.name}</h6>
                    <h6 className='w-[25%] truncate p-1 border-r'>{c?.office_file_no} / {c?.case_no}</h6>
                    <h6 className='w-[25%] truncate p-1 border-r'>{c?.case_details}</h6>
                    <h6 className='w-[25%] truncate p-1 '>{c?.status}</h6>
                </div>
                  ))}

                  {data?.today_office_file?.map((c,i)=>(
                    <div key={i} onClick={()=>navigator('/opinion_files/list/detail',{state:c})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
                    <h6 className='w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r'>{c?.client?.name}</h6>
                    <h6 className='w-[25%] truncate p-1 border-r'>{c?.office_file_no} </h6>
                    <h6 className='w-[25%] truncate p-1 border-r'>{c?.work_details}</h6>
                    <h6 className='w-[25%] truncate p-1 '>{c?.status}</h6>
                </div>
                  ))}
                </div>}


             </div>

             <div className='border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2'>
              <h6 className='text-[13px] font-[700]'>Upcoming Reminder ({data?.today_reminder_total})</h6>

                 {data?.today_reminder_total === 0 &&
                 <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
                  <IoCalendarClearOutline size={40} color="#ddd" />
                  <h6 className='text-[11px] mt-2'>No Reminder found for today</h6>
                  <h6 className='text-[11px] text-center'>We found that you have not added any reminder for today session.</h6>
                 </div>}

               {data?.today_reminder_total > 0 &&
                 <div className='overflow-y-scroll min-h-[85%] max-h-[85%]'>
                    <div className='flex text-[11px] border mt-2'>
                        <h6 className='w-[50%] p-1 border-r'>Case Info</h6>
                        <h6 className='w-[25%] p-1 border-r'>Title</h6>
                        <h6 className='w-[25%] p-1 '>Summary</h6>
                      </div>

                    {data?.today_reminder?.map((r,i)=>(
                        <div key={i} onClick={()=>r?.type === 'case' ?  navigator(`/case/${r?.case?.case_type?.name == "Civil Court" ? "civil_court" : "high_court"}/detail`,{state:r?.case}) : navigator('/opinion_files/list/detail',{state:r?.opinion_file})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
                            <h6 className='w-[50%] truncate p-1 font-[700] text-sky-800 border-r'> Type : <span className='underline'>{r?.type}</span> 
                            {r?.type === 'opinion_file' && 
                              <div className='break-all'>
                                <span>Opinion Info : {r?.opinion_file?.office_file_no} / {r?.opinion_file?.work_details}  <br></br> 
                                Client Info : {r?.opinion_file?.client?.name}  <br></br>  
                                {r?.opinion_file?.client?.mobile}
                                </span>
                              </div>
                            }

                            {r?.type === 'case' && 
                              <div className='break-all'>
                                <span>Case  Info : {r?.case?.office_file_no} / {r?.case?.case_details}  <br></br> 
                                Client Info : {r?.case?.client?.name}  <br></br>  
                                {r?.case?.client?.mobile}
                                </span>
                              </div>
                            }
                            </h6>
                            <h6 className='w-[25%] truncate p-1 font-[700] border-r'>{r?.title}</h6>
                            <h6 className='w-[25%] truncate p-1 '>{r?.description} </h6>
                        </div>
                    ))}
                 </div>}
             </div>

             <div className='border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2'>
              <h6 className='text-[13px] font-[700]'>Today Bill Raised vs Collection</h6>

                  {(data?.today_bills?.totalAmountWithGst === 0 && data?.today_payment?.totalAmountWithGst === 0) && 
                  <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
                      <IoCalendarClearOutline size={40} color="#ddd" />
                      <h6 className='text-[11px] mt-2'>No Bill or Payment Collection Made</h6>
                      <h6 className='text-[11px] text-center mt-1'>We found that you have not created any bill or payment collection made for today.</h6>
                  </div>}

                  {(data?.today_bills?.totalAmountWithGst > 0 || data?.today_payment?.totalAmountWithGst > 0) &&
                  <div className=''>
                    <ReactApexChart options={chartOptions2} size={'10'} series={chartOptions2?.series} type="bar" width={'100%'}  height={150} />
                  </div>}

             </div>


             
           </div>

          {/* ----------------------------------------------- */}
          {/* TOP STATS (NO CHANGE) */}
          {/* ----------------------------------------------- */}
          {/* KEEP YOUR EXISTING STATS JSX HERE... */}


          {/* ----------------------------------------------- */}
          {/* WEEKLY CALENDAR + HEARINGS */}
          {/* ----------------------------------------------- */}
          <div className="mt-5 md:mt-3">

            {/* Calendar button */}
            <div className="flex justify-end mb-3">
              <button
                onClick={openCalendar}
                className="flex items-center gap-2 px-3 py-1 text-sm border rounded-md shadow-sm"
              >
                <IoCalendarClearOutline size={16} />
                Calendar
              </button>
            </div>

            {/* ----------------------------------------- */}
            {/* WEEKLY STRIP */}
            {/* ----------------------------------------- */}
            <div className="p-3 mb-3 border rounded-md border-slate-100">

              <div className="flex items-center justify-between mb-3">

                <button
                  className="px-3 py-1 text-xs border rounded-full"
                  onClick={() => {
                    const today = moment();
                    setSelectedDate(today);
                    generateWeek(today);
                  }}
                >
                  Today
                </button>

                <div className="flex items-center space-x-2">
                  <button
                    className="px-2 py-1 text-xs border rounded-full"
                    onClick={goPrevWeek}
                  >
                    ◀
                  </button>

                  <span className="text-sm font-semibold">
                    {selectedDate.format("MMM YYYY")}
                  </span>

                  <button
                    className="px-2 py-1 text-xs border rounded-full"
                    onClick={goNextWeek}
                  >
                    ▶
                  </button>
                </div>

                <div />
              </div>

              <div className="flex justify-between">
                {weekDates.map((d, i) => {
                  const isSelected = d.isSame(selectedDate, "day");
                  const isToday = d.isSame(moment(), "day");

                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedDate(d)}
                      className={`w-[13%] text-center cursor-pointer rounded-md p-2 border 
                        ${isSelected ? "bg-blue-500 text-white" : "bg-gray-50"} 
                        ${isToday && !isSelected ? "border-blue-500" : ""}
                      `}
                    >
                      <h6 className="text-[13px] font-semibold">
                        {d.format("DD MMM")}
                      </h6>
                      <h6 className="text-[11px]">
                        {d.format("ddd").toUpperCase()}
                      </h6>

                      {/* dot for hearing */}
                      {hasHearingOn(d.toDate()) && !isSelected && (
                        <div className="flex justify-center mt-1">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ----------------------------------------- */}
            {/* HEARINGS LIST */}
            {/* ----------------------------------------- */}
            <div className="p-3 border rounded-md border-slate-100">
              <h6 className="text-[13px] font-[700] mb-2">
                Hearings for {selectedDate.format("DD MMM, ddd")} (
                {filteredHearings.length})
              </h6>

              {filteredHearings.length === 0 && (
                <div className="flex flex-col my-8 w-[80%] mx-auto items-center">
                  <IoCalendarClearOutline size={40} color="#ddd" />
                  <h6 className="text-[11px] mt-2">No hearings for this date</h6>
                </div>
              )}

              {filteredHearings.length > 0 && (
                <div className="overflow-y-scroll min-h-[85%] max-h-[85%]">
                  <div className="flex text-[11px] border mt-2">
                    <h6 className="w-[40%] p-1 border-r">Case</h6>
                    <h6 className="w-[30%] p-1 border-r">Date</h6>
                    <h6 className="w-[30%] p-1">Stage</h6>
                  </div>

                  {filteredHearings.map((h, i) => (
                    <div
                      key={i}
                      onClick={() =>
                        navigator(
                          `/case/${
                            h?.case?.case_type?.name == "Civil Court"
                              ? "civil_court"
                              : "high_court"
                          }/detail`,
                          { state: h?.case }
                        )
                      }
                      className="flex cursor-pointer text-[11px] border-l border-r border-b"
                    >
                      <h6 className="w-[40%] truncate p-1 font-[700] underline text-sky-800 border-r">
                        {h?.client?.name}
                      </h6>
                      <h6 className="w-[30%] truncate p-1 border-r">
                        {moment(h?.next_hearing_date).format("LL")}
                      </h6>
                      <h6 className="w-[30%] truncate p-1">
                        {h?.stage?.name || "---"}
                      </h6>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* ------------------------------------------------ */}
    {/* FULL SCREEN GLASS CALENDAR MODAL */}
    {/* ------------------------------------------------ */}
    {isCalendarOpen && (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[999]">

        <div className="bg-white shadow-xl rounded-xl p-5 w-[90%] md:w-[60%] max-h-[90vh] overflow-hidden relative">

          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              className="px-3 py-1 text-xs border rounded-full"
              onClick={closeCalendar}
            >
              Close
            </button>

            <div className="flex items-center gap-3">
              <button
                className="px-3 py-1 text-xs border rounded-full"
                onClick={goPrevMonth}
              >
                ◀
              </button>

              <h2 className="text-lg font-semibold">{calendarMonthLabel}</h2>

              <button
                className="px-3 py-1 text-xs border rounded-full"
                onClick={goNextMonth}
              >
                ▶
              </button>
            </div>

            <div />
          </div>

          {/* Calendar */}
          <Calendar
            onChange={handleCalendarChange}
            value={calendarDate}
            tileContent={renderTileContent}
            className="w-full rounded-lg shadow-md"
          />
        </div>
      </div>
    )}
  </div>
);
}

export default Dashboard;












//==>> Mixed


// import React,{useState,useEffect} from 'react'
// import {RiCustomerService2Line} from 'react-icons/ri';
// import DashboardMenu from './DashboardMenu'
// // import { GetTodayLeadReminderService, getInsentiveTargetAchieved, getTargetAchieved } from '../../services/LeadServices';
// import { BiLandscape } from 'react-icons/bi';
// import { useNavigate } from 'react-router-dom';
// import { GetHomeDataService } from '../../services/HomeDataServices';

// import { PiSuitcaseSimple } from "react-icons/pi";
// import { TbBriefcaseOff } from "react-icons/tb";
// import { TbDeviceAirpodsCase } from "react-icons/tb";
// import { MdOutlineDriveFileMove,MdOutlineDriveFileMoveRtl,MdOutlinePayment } from "react-icons/md";
// import { FiFileText } from "react-icons/fi";
// import { RiBillLine } from "react-icons/ri";
// import { LuCalendarClock } from "react-icons/lu";
// import { AiOutlineAlert } from "react-icons/ai";
// import { TbMoneybag } from "react-icons/tb";
// import ReactApexChart from 'react-apexcharts';
// import { IoCalendarClearOutline } from "react-icons/io5";

// function Dashboard() {


//   const navigator = useNavigate()
//   const [data,setdata] = useState({
//       total_case:0,
//       total_case_pending:0,
//       total_case_progress:0,
//       total_case_completed:0,
//       total_case_hold:0,

//       total_opinion_file:0,
//       total_opinion_file_pending:0,
//       total_opinion_file_progress:0,
//       total_opinion_file_completed:0,
//       total_opinion_file_hold:0,

//       bills:[],
//       billamount:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       payment:[],
//       paymentamount:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       reminder:[],
//       reminder_active:[],
//       today_case:[],
//       today_office_file:[],
//       today_case_total:0,
//       today_bills:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       today_payment:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       today_reminder:[],
//       today_reminder_total:0

//   })
  
//   useEffect(()=>{
//     getData()
//   },[])

//   const chartOptions = {
//     plotOptions: {
//       color:['#000','#000'],
//       size:'40%',
//       radialBar: {
//           inverseOrder: false,
//           startAngle: 0,
//           endAngle: 360,
//           offsetX: 0,
//           offsetY: 0,
//           hollow: {
//               margin: 5,
//               size: '50%',
//               color: '#f2f2f2',
//               image: undefined,
//               imageWidth: 150,
//               imageHeight: 150,
//               imageOffsetX: 0,
//               imageOffsetY: 0,
//               imageClipped: true,
//               position: 'front',
             
//           },
//           track: {
//               show: true,
//               startAngle: undefined,
//               endAngle: undefined,
//               background: '#f2f2f2',
//               strokeWidth: '50%',
//               opacity: 1,
//               margin: 5, 
//               dropShadow: {
//                   enabled: false,
//                   top: 0,
//                   left: 0,
//                   opacity: 1
//               }
//           },
//           dataLabels: {
//               show: true,
//               name: {
//                   show: true,
//                   fontSize: '12px',
//                   fontFamily: undefined,
//                   fontWeight: 400,
//                   color: undefined,
//                   color:'#000',
//                 },
//                 value: {
//                   show: true,
//                   fontSize: '14px',
//                   fontFamily: undefined,
//                   fontWeight: 900,
//                   color: undefined,
//                   offsetY: 1,
//                   formatter: function (val) {
//                     return val + '%'
//                   }
//                 },
                
//           },
          
//       },
//     },
//     labels: ['Revenue Forecast'],
//     fill:{
//       colors: ['#008ef2'],
//     }
//   }

//   const [chartOptions1,setchartOptions1] = useState({
//     series: [],

//     chart: {
//       toolbar: {
//         show: false
//       }
//     },
//     plotOptions: {
      
//       bar: {
//         horizontal: false,
//         columnWidth: '55%',
//         endingShape: 'rounded',
//         dataLabels: {
//           position: 'top', // top, center, bottom
//         },
        
//       },
//     },
//     dataLabels: {
//       enabled: false
//     },
//     grid:{
//       show: true,
//       borderColor: '#fafafa',
//     },
  
//     stroke: {
//       // show: true,
//       width: 1,
      
//       curve: 'smooth',
//       colors: ['transparent']
//     },
//     xaxis: {
//       categories:    ['Case', 'Opinion File',],
//     },
//     yaxis: {
//       title: {
//         text: 'Count'
//       }
//     },
//     fill: {
//       opacity: 1
//     },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return  val
//         }
//       }
//     },
//     fill:['#027cd1']
//   })

//   const [chartOptions2,setchartOptions2] = useState({
//     series: [],

//     chart: {
//       toolbar: {
//         show: false
//       }
//     },
//     plotOptions: {
      
//       bar: {
//         horizontal: false,
//         columnWidth: '55%',
//         endingShape: 'rounded',
//         dataLabels: {
//           position: 'top', // top, center, bottom
//         },
        
//       },
//     },
//     dataLabels: {
//       enabled: false
//     },
//     grid:{
//       show: true,
//       borderColor: '#fafafa',
//     },
  
//     stroke: {
//       // show: true,
//       width: 1,
      
//       curve: 'smooth',
//       colors: ['transparent']
//     },
//     xaxis: {
//       categories: ['Bill Amount', 'Payment Collection'],
//     },
//     yaxis: {
//       title: {
//         text: 'Count'
//       }
//     },
//     fill: {
//       opacity: 1
//     },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return  val
//         }
//       }
//     },
//     fill:['#027cd1']
//   })

//   async function getData(){
//     const response = await GetHomeDataService()

//     let chartOptions = []

//     let d = response?.data?.data

//     let arr = [
//       {
//         name:"Total",
//         data:[d?.total_case,d?.total_opinion_file]
//       },
//       {
//         name:"Completed",
//         data:[d?.total_case_completed,d?.total_opinion_file_completed]
//       },
//       {
//         name:"Pending",
//         data:[d?.total_case_pending,d?.total_opinion_file_pending]
//       },
//       {
//         name:"Progress",
//         data:[d?.total_case_progress,d?.total_opinion_file_progress]
//       },
//       {
//         name:"Hold",
//         data:[d?.total_case_hold,d?.total_opinion_file_completed]
//       },
    
//     ]
//     chartOptions.push('Cases')
//     chartOptions.push('Opinion File')

//     setchartOptions1({
//       ...chartOptions1,
//       series:arr,
//       xaxis:{
//         categories:chartOptions
//       }
//     })

//     setchartOptions2({
//       ...chartOptions2,
//       series:[{
//         name:"Amount",
//         data:[
//           d?.today_bills?.totalAmountWithGst,
//           d?.today_payment?.totalAmountWithGst,
//         ]
//       }],
//     })
//     setdata(response?.data?.data)
//   }

//   let rupeeIndian = Intl.NumberFormat("en-IN", {
//     style: "currency",
//     currency: "INR",
//   });

//   return (
//     <div className='md:h-screen md:min-h-screen md:max-h-screen md:overflow-hidden'>

//       <div className='md:flex'>
//         <DashboardMenu />
//         <div className='md:w-[100%] md:w-[86%] flex md:h-screen md:min-h-screen md:max-h-screen md:overflow-y-scroll'>
//           <div className='w-[100%] px-4 pt-5 '>
           
//            {/* section 1  */}
//            <div className='grid items-center grid-cols-2 border-t border-b border-l border-r md:grid-cols-6 justify-evenly border-slate-100'>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><PiSuitcaseSimple size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> Total Case </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbDeviceAirpodsCase size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> Active Case </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case_pending + data?.total_case_progress))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbBriefcaseOff size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> InActive Case </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case_hold))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlineDriveFileMove size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> Total Opinion File </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><FiFileText size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> Active Opinion File </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file_pending + data?.total_opinion_file_progress))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlineDriveFileMoveRtl size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> InActive Opinion File </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file_hold))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' onClick={()=>navigator('/bills/list')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><RiBillLine size={23} className='p-1 mr-2 text-purple-500 bg-purple-200' /> Total Bills </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.bills))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlinePayment size={23} className='p-1 mr-2 text-purple-500 bg-purple-200' /> Total Payment </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.payment))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><AiOutlineAlert size={23} className='p-1 mr-2 text-red-500 bg-red-200' /> Total Reminder </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.reminder))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><LuCalendarClock size={23} className='p-1 mr-2 text-red-500 bg-red-200' /> Active Reminder </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.reminder_active))?.split('.')[0].slice(1)}</h6>
//                   </div>

//                   <div className='w-full px-2 py-2 border-r border-slate-100'>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbMoneybag size={23} className='p-1 mr-2 text-green-500 bg-green-200' /> Total Revenue </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.billamount?.totalAmountWithGst))?.split('.')[0].slice(0)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbMoneybag size={23} className='p-1 mr-2 text-green-500 bg-green-200' /> Collect Revenue </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.paymentamount?.totalAmountWithGst))?.split('.')[0].slice(0)}</h6>
//                   </div>
//            </div>  

//            {/* section 2  */}
//            <div className='mt-5 md:flex md:mt-2'>
//               <div className=' md:w-[29.5%] md:mr-[0.5%] border border-slate-100 p-5 px-3 py-2'>

//               <h6 className='text-[13px] font-[700]'>Revenue Generated</h6>

//               <div className='flex '>
//                 <div className='-mt-5 -ml-14'>
//                 <ReactApexChart options={chartOptions} size={'10'} series={
//                   [Math.floor((data?.paymentamount?.totalAmountWithGst/data?.billamount?.totalAmountWithGst)*100)]} type="radialBar" width={240} height={240} />
//                 </div>
//                 <div className='w-[100%] -ml-[40px] -mt-8 -ml-2'>

//                 <div className='relative w-full mt-16'>
//                   <h6 className='text-[11px] font-[600]'>Bill Amount </h6>
//                     <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-2 dark:bg-gray-700">
//                       <div className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none w-[100%]`} style={{width:data?.billamount?.totalAmountWithGst > 0 ? '100%' : '0%'}}  >{data?.billamount?.totalAmountWithGst > 0 ? '100%' : '0%'}</div>
//                     </div>
//                     <h6 className='text-[11px] font-[700] absolute top-0 right-0'>{rupeeIndian?.format(JSON.stringify(data?.billamount?.totalAmountWithGst))?.split('.')[0]}</h6>

//                 </div>

//                 <div className='relative w-full mt-4 ml-0'>
//                   <h6 className='text-[11px] font-[600]'>Payment Collected </h6>
//                     <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-4 dark:bg-gray-700">
//                       <div className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none `} style={{width: data?.paymentamount?.totalAmountWithGst  >= data?.billamount?.totalAmountWithGst ? '100%' :  `${Math.round((data?.paymentamount?.totalAmountWithGst/data?.billamount?.totalAmountWithGst))*100}%`}} >{data?.paymentamount?.totalAmountWithGst > 0 ?  Math.round((data?.paymentamount?.totalAmountWithGst / data?.billamount?.totalAmountWithGst)*100): 0}%</div>
//                     </div>
//                     <h6 className='text-[11px] font-[700] absolute top-0 right-0'>{rupeeIndian?.format(JSON.stringify(data?.paymentamount?.totalAmountWithGst))?.split('.')[0]}</h6>

//                 </div>
              
//                 </div>
//               </div>

//               </div>

//               <div className={`border mt-2 md:mt-0 md:w-[69.5%] md:ml-[0.5%]   border-slate-100 px-3 py-2 relative`}>
//                     <h1 className='text-[13px] font-[700]'>Case / Opinion File</h1>
//                     <ReactApexChart options={chartOptions1} size={'10'} series={chartOptions1.series} type="bar" width={'100%'}  height={220} />
//               </div>
//            </div>

//            {/* section 3  */}
//            <div className='mt-5 md:flex md:mt-2'>

//            <div className='border md:w-[33%] border-slate-100 p-2'>
//               <h6 className='text-[13px] font-[700]'>Today Case / Opinion File Register ({data?.today_case_total})</h6>

//                 {data?.today_case_total === 0 &&
//                  <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//                  <IoCalendarClearOutline size={40} color="#ddd" />
//                  <h6 className='text-[11px] mt-2'>No Case / Opinion File found for today</h6>
//                  <h6 className='text-[11px] text-center mt-1'>We found that you have not added any case or opinion file for today session.</h6>
//                  </div>
//                 }
              
//                 {(data?.today_case?.length > 0 || data?.today_office_file?.length > 0) &&
//                 <div className='overflow-y-scroll min-h-[85%] max-h-[85%]'>
//                   <div className='flex text-[11px] border mt-2'>
//                     <h6 className='w-[25%] p-1 border-r'>Client</h6>
//                     <h6 className='w-[25%] p-1 border-r'>Office File No / Case No</h6>
//                     <h6 className='w-[25%] p-1 border-r'>Case Work</h6>
//                     <h6 className='w-[25%] p-1 '>Status</h6>
//                   </div>

//                   {data?.today_case?.map((c,i)=>(
//                     <div key={i} onClick={()=>navigator('/case/civil_court/detail',{state:c})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
//                     <h6 className='w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r'>{c?.client?.name}</h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.office_file_no} / {c?.case_no}</h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.case_details}</h6>
//                     <h6 className='w-[25%] truncate p-1 '>{c?.status}</h6>
//                 </div>
//                   ))}

//                   {data?.today_office_file?.map((c,i)=>(
//                     <div key={i} onClick={()=>navigator('/opinion_files/list/detail',{state:c})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
//                     <h6 className='w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r'>{c?.client?.name}</h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.office_file_no} </h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.work_details}</h6>
//                     <h6 className='w-[25%] truncate p-1 '>{c?.status}</h6>
//                 </div>
//                   ))}
//                 </div>}


//              </div>

//              <div className='border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2'>
//               <h6 className='text-[13px] font-[700]'>Upcoming Reminder ({data?.today_reminder_total})</h6>

//                  {data?.today_reminder_total === 0 &&
//                  <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//                   <IoCalendarClearOutline size={40} color="#ddd" />
//                   <h6 className='text-[11px] mt-2'>No Reminder found for today</h6>
//                   <h6 className='text-[11px] text-center'>We found that you have not added any reminder for today session.</h6>
//                  </div>}

//                {data?.today_reminder_total > 0 &&
//                  <div className='overflow-y-scroll min-h-[85%] max-h-[85%]'>
//                     <div className='flex text-[11px] border mt-2'>
//                         <h6 className='w-[50%] p-1 border-r'>Case Info</h6>
//                         <h6 className='w-[25%] p-1 border-r'>Title</h6>
//                         <h6 className='w-[25%] p-1 '>Summary</h6>
//                       </div>

//                     {data?.today_reminder?.map((r,i)=>(
//                         <div key={i} onClick={()=>r?.type === 'case' ?  navigator(`/case/${r?.case?.case_type?.name == "Civil Court" ? "civil_court" : "high_court"}/detail`,{state:r?.case}) : navigator('/opinion_files/list/detail',{state:r?.opinion_file})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
//                             <h6 className='w-[50%] truncate p-1 font-[700] text-sky-800 border-r'> Type : <span className='underline'>{r?.type}</span> 
//                             {r?.type === 'opinion_file' && 
//                               <div className='break-all'>
//                                 <span>Opinion Info : {r?.opinion_file?.office_file_no} / {r?.opinion_file?.work_details}  <br></br> 
//                                 Client Info : {r?.opinion_file?.client?.name}  <br></br>  
//                                 {r?.opinion_file?.client?.mobile}
//                                 </span>
//                               </div>
//                             }

//                             {r?.type === 'case' && 
//                               <div className='break-all'>
//                                 <span>Case  Info : {r?.case?.office_file_no} / {r?.case?.case_details}  <br></br> 
//                                 Client Info : {r?.case?.client?.name}  <br></br>  
//                                 {r?.case?.client?.mobile}
//                                 </span>
//                               </div>
//                             }
//                             </h6>
//                             <h6 className='w-[25%] truncate p-1 font-[700] border-r'>{r?.title}</h6>
//                             <h6 className='w-[25%] truncate p-1 '>{r?.description} </h6>
//                         </div>
//                     ))}
//                  </div>}
//              </div>

//              <div className='border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2'>
//               <h6 className='text-[13px] font-[700]'>Today Bill Raised vs Collection</h6>

//                   {(data?.today_bills?.totalAmountWithGst === 0 && data?.today_payment?.totalAmountWithGst === 0) && 
//                   <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//                       <IoCalendarClearOutline size={40} color="#ddd" />
//                       <h6 className='text-[11px] mt-2'>No Bill or Payment Collection Made</h6>
//                       <h6 className='text-[11px] text-center mt-1'>We found that you have not created any bill or payment collection made for today.</h6>
//                   </div>}

//                   {(data?.today_bills?.totalAmountWithGst > 0 || data?.today_payment?.totalAmountWithGst > 0) &&
//                   <div className=''>
//                     <ReactApexChart options={chartOptions2} size={'10'} series={chartOptions2?.series} type="bar" width={'100%'}  height={150} />
//                   </div>}

//              </div>


             
//            </div>

//           </div>
//         </div> 

        
//       </div>
//     </div>
    
//   )
// }

// export default Dashboard








//=========>>> below is the advanced code jan 2026







// CHUNK 1 – LOGIC + STATES + FULL CALENDAR SUPPORT
// import React, { useState, useEffect, useMemo } from "react";
// import DashboardMenu from "./DashboardMenu";
// import { useNavigate } from "react-router-dom";
// import { GetHomeDataService } from "../../services/HomeDataServices";
// import { GetActivityService } from "../../services/ActivityServices";

// import { PiSuitcaseSimple } from "react-icons/pi";
// import { TbBriefcaseOff, TbDeviceAirpodsCase, TbMoneybag } from "react-icons/tb";
// import {
//   MdOutlineDriveFileMove,
//   MdOutlineDriveFileMoveRtl,
//   MdOutlinePayment,
// } from "react-icons/md";
// import { FiFileText } from "react-icons/fi";
// import { RiBillLine } from "react-icons/ri";
// import { LuCalendarClock } from "react-icons/lu";
// import { AiOutlineAlert } from "react-icons/ai";
// import { IoCalendarClearOutline } from "react-icons/io5";

// import ReactApexChart from "react-apexcharts";
// import Calendar from "react-calendar";
// import "react-calendar/dist/Calendar.css";
// import moment from "moment";

// function Dashboard() {
//   const navigator = useNavigate();

//   // ----------------------------------------
//   // MAIN DASHBOARD DATA STATE
//   // ----------------------------------------
//   const [data, setdata] = useState({
//     total_case: 0,
//     total_case_pending: 0,
//     total_case_progress: 0,
//     total_case_completed: 0,
//     total_case_hold: 0,

//     total_opinion_file: 0,
//     total_opinion_file_pending: 0,
//     total_opinion_file_progress: 0,
//     total_opinion_file_completed: 0,
//     total_opinion_file_hold: 0,

//     bills: [],
//     billamount: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     payment: [],
//     paymentamount: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     reminder: [],
//     reminder_active: [],
//     today_case: [],
//     today_office_file: [],
//     today_case_total: 0,
//     today_bills: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     today_payment: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     today_reminder: [],
//     today_reminder_total: 0,
//   });

//   // ----------------------------------------
//   // HEARING LIST STATE (from activity service)
//   // ----------------------------------------
//   const [hearingList, setHearingList] = useState([]);

//   // ----------------------------------------
//   // WEEKLY CALENDAR STATES
//   // ----------------------------------------
//   const [selectedDate, setSelectedDate] = useState(moment().startOf("day"));
//   const [weekDates, setWeekDates] = useState([]);
//   const [filteredHearings, setFilteredHearings] = useState([]);

//   // ----------------------------------------
//   // FULL-CALENDAR MODAL STATES
//   // ----------------------------------------
//   const [isCalendarOpen, setIsCalendarOpen] = useState(false);
//   const [calendarDate, setCalendarDate] = useState(new Date()); // controls month in modal

//   // Map of YYYY-MM-DD -> hearings[] (for dots on calendar)
//   const hearingDatesMap = useMemo(() => {
//     const map = {};
//     hearingList.forEach((h) => {
//       if (!h?.next_hearing_date) return;
//       const key = moment(h.next_hearing_date).format("YYYY-MM-DD");
//       if (!map[key]) map[key] = [];
//       map[key].push(h);
//     });
//     return map;
//   }, [hearingList]);

//   const hasHearingOn = (date) => {
//     const key = moment(date).format("YYYY-MM-DD");
//     return !!hearingDatesMap[key];
//   };

//   // ----------------------------------------
//   // GENERATE WEEK (Sun → Sat)
//   // ----------------------------------------
//   function generateWeek(date) {
//     const startOfWeek = moment(date).startOf("week"); // Sunday
//     const week = [];
//     for (let i = 0; i < 7; i++) {
//       week.push(moment(startOfWeek).add(i, "days"));
//     }
//     setWeekDates(week);
//   }

//   // ----------------------------------------
//   // WEEK NAVIGATION
//   // ----------------------------------------
//   function goPrevWeek() {
//     const newDate = moment(selectedDate).subtract(7, "days");
//     setSelectedDate(newDate);
//     generateWeek(newDate);
//   }

//   function goNextWeek() {
//     const newDate = moment(selectedDate).add(7, "days");
//     setSelectedDate(newDate);
//     generateWeek(newDate);
//   }

//   // ----------------------------------------
//   // FILTER HEARINGS WHEN SELECTED DATE CHANGES
//   // ----------------------------------------
//   useEffect(() => {
//     const filtered = hearingList.filter((h) =>
//       moment(h.next_hearing_date).isSame(selectedDate, "day")
//     );
//     setFilteredHearings(filtered);
//   }, [selectedDate, hearingList]);

//   // ----------------------------------------
//   // FETCH UPCOMING HEARINGS FROM ACTIVITY SERVICE
//   // ----------------------------------------
//   async function getUpcomingHearings() {
//     try {
//       const res = await GetActivityService("case");
//       const list = res?.data?.datas || [];

//       const today = new Date();
//       today.setHours(0, 0, 0, 0);

//       const upcoming = list.filter(
//         (a) =>
//           a?.next_hearing_date && new Date(a.next_hearing_date) >= today
//       );

//       upcoming.sort(
//         (a, b) => new Date(a.next_hearing_date) - new Date(b.next_hearing_date)
//       );

//       setHearingList(upcoming);
//     } catch (err) {
//       console.log("Error fetching hearings", err);
//     }
//   }

//   // ----------------------------------------
//   // FETCH DASHBOARD DATA
//   // ----------------------------------------
//   async function getData() {
//     const response = await GetHomeDataService();
//     const d = response?.data?.data || {};

//     const arr = [
//       { name: "Total", data: [d?.total_case, d?.total_opinion_file] },
//       {
//         name: "Completed",
//         data: [d?.total_case_completed, d?.total_opinion_file_completed],
//       },
//       {
//         name: "Pending",
//         data: [d?.total_case_pending, d?.total_opinion_file_pending],
//       },
//       {
//         name: "Progress",
//         data: [d?.total_case_progress, d?.total_opinion_file_progress],
//       },
//       {
//         name: "Hold",
//         data: [d?.total_case_hold, d?.total_opinion_file_hold],
//       },
//     ];

//     setchartOptions1((prev) => ({
//       ...prev,
//       series: arr,
//       xaxis: { categories: ["Cases", "Opinion File"] },
//     }));

//     setchartOptions2((prev) => ({
//       ...prev,
//       series: [
//         {
//           name: "Amount",
//           data: [
//             d?.today_bills?.totalAmountWithGst || 0,
//             d?.today_payment?.totalAmountWithGst || 0,
//           ],
//         },
//       ],
//     }));

//     setdata(d);
//   }

//   // ----------------------------------------
//   // CHART STATES (BAR CHARTS)
//   // ----------------------------------------
//   const [chartOptions1, setchartOptions1] = useState({
//     series: [],
//     chart: { toolbar: { show: false } },
//     plotOptions: {
//       bar: {
//         horizontal: false,
//         columnWidth: "55%",
//         endingShape: "rounded",
//       },
//     },
//     dataLabels: { enabled: false },
//     grid: { show: true, borderColor: "#fafafa" },
//     stroke: { width: 1, curve: "smooth", colors: ["transparent"] },
//     xaxis: { categories: [] },
//     yaxis: { title: { text: "Count" } },
//     fill: { opacity: 1 },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return val;
//         },
//       },
//     },
//     fill: ["#027cd1"],
//   });

//   const [chartOptions2, setchartOptions2] = useState({
//     series: [],
//     chart: { toolbar: { show: false } },
//     plotOptions: {
//       bar: {
//         horizontal: false,
//         columnWidth: "55%",
//         endingShape: "rounded",
//       },
//     },
//     dataLabels: { enabled: false },
//     grid: { show: true, borderColor: "#fafafa" },
//     stroke: { width: 1, curve: "smooth", colors: ["transparent"] },
//     xaxis: { categories: ["Bill Amount", "Payment Collection"] },
//     yaxis: { title: { text: "Count" } },
//     fill: { opacity: 1 },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return val;
//         },
//       },
//     },
//     fill: ["#027cd1"],
//   });

//   // ----------------------------------------
//   // RUN ON MOUNT
//   // ----------------------------------------
//   useEffect(() => {
//     getData();
//     getUpcomingHearings();
//     generateWeek(moment());
//   }, []);

//   // ----------------------------------------
//   // FORMATTER
//   // ----------------------------------------
//   let rupeeIndian = Intl.NumberFormat("en-IN", {
//     style: "currency",
//     currency: "INR",
//   });

//   // ----------------------------------------
//   // RADIAL CHART OPTIONS + REVENUE %
//   // ----------------------------------------
//   const chartOptions = {
//     plotOptions: {
//       color: ["#000", "#000"],
//       size: "40%",
//       radialBar: {
//         inverseOrder: false,
//         startAngle: 0,
//         endAngle: 360,
//         offsetX: 0,
//         offsetY: 0,
//         hollow: {
//           margin: 5,
//           size: "50%",
//           color: "#f2f2f2",
//           image: undefined,
//           imageWidth: 150,
//           imageHeight: 150,
//           imageOffsetX: 0,
//           imageOffsetY: 0,
//           imageClipped: true,
//           position: "front",
//         },
//         track: {
//           show: true,
//           background: "#f2f2f2",
//           strokeWidth: "50%",
//           opacity: 1,
//           margin: 5,
//           dropShadow: {
//             enabled: false,
//             top: 0,
//             left: 0,
//             opacity: 1,
//           },
//         },
//         dataLabels: {
//           show: true,
//           name: {
//             show: true,
//             fontSize: "12px",
//             fontWeight: 400,
//             color: "#000",
//           },
//           value: {
//             show: true,
//             fontSize: "14px",
//             fontWeight: 900,
//             offsetY: 1,
//             formatter: function (val) {
//               return val + "%";
//             },
//           },
//         },
//       },
//     },
//     labels: ["Revenue Forecast"],
//     fill: {
//       colors: ["#008ef2"],
//     },
//   };

//   const revenuePercent =
//     data?.billamount?.totalAmountWithGst > 0
//       ? Math.floor(
//           (data?.paymentamount?.totalAmountWithGst /
//             data?.billamount?.totalAmountWithGst) *
//             100
//         )
//       : 0;

//   // ----------------------------------------
//   // FULL-CALENDAR HELPERS (MODAL)
//   // ----------------------------------------
//   const openCalendar = () => setIsCalendarOpen(true);
//   const closeCalendar = () => setIsCalendarOpen(false);

//   const goPrevMonth = () => {
//     setCalendarDate((prev) => moment(prev).subtract(1, "month").toDate());
//   };

//   const goNextMonth = () => {
//     setCalendarDate((prev) => moment(prev).add(1, "month").toDate());
//   };

//   const handleCalendarChange = (date) => {
//     // When user picks a date in full calendar, sync weekly strip + list
//     const m = moment(date).startOf("day");
//     setCalendarDate(date);
//     setSelectedDate(m);
//     generateWeek(m);
//     setIsCalendarOpen(false);
//   };

//   // Dots under days that have hearings
//   const renderTileContent = ({ date, view }) => {
//     if (view !== "month") return null;
//     if (!hasHearingOn(date)) return null;

//     return (
//       <div className="flex justify-center mt-1">
//         <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
//       </div>
//     );
//   };

//   const calendarMonthLabel = moment(calendarDate).format("MMMM YYYY");

//   // ---- JSX starts in CHUNK 2 ----
// // CHUNK 2 — JSX (UI + MODAL + CALENDAR + WEEK STRIP)

// return (
//   <div className="md:h-screen md:min-h-screen md:max-h-screen md:overflow-hidden">
//     <div className="md:flex">
//       <DashboardMenu />

//       <div className="md:w-[100%] md:w-[86%] flex md:h-screen md:min-h-screen md:max-h-screen md:overflow-y-scroll">
//         <div className="w-[100%] px-4 pt-5 ">

//           {/* ----------------------------------------------- */}
//           {/* TOP STATS (NO CHANGE) */}
//           {/* ----------------------------------------------- */}
//           {/* KEEP YOUR EXISTING STATS JSX HERE... */}


//           {/* ----------------------------------------------- */}
//           {/* WEEKLY CALENDAR + HEARINGS */}
//           {/* ----------------------------------------------- */}
//           <div className="mt-5 md:mt-3">

//             {/* Calendar button */}
//             <div className="flex justify-end mb-3">
//               <button
//                 onClick={openCalendar}
//                 className="flex items-center gap-2 px-3 py-1 text-sm border rounded-md shadow-sm"
//               >
//                 <IoCalendarClearOutline size={16} />
//                 Calendar
//               </button>
//             </div>

//             {/* ----------------------------------------- */}
//             {/* WEEKLY STRIP */}
//             {/* ----------------------------------------- */}
//             <div className="p-3 mb-3 border rounded-md border-slate-100">

//               <div className="flex items-center justify-between mb-3">

//                 <button
//                   className="px-3 py-1 text-xs border rounded-full"
//                   onClick={() => {
//                     const today = moment();
//                     setSelectedDate(today);
//                     generateWeek(today);
//                   }}
//                 >
//                   Today
//                 </button>

//                 <div className="flex items-center space-x-2">
//                   <button
//                     className="px-2 py-1 text-xs border rounded-full"
//                     onClick={goPrevWeek}
//                   >
//                     ◀
//                   </button>

//                   <span className="text-sm font-semibold">
//                     {selectedDate.format("MMM YYYY")}
//                   </span>

//                   <button
//                     className="px-2 py-1 text-xs border rounded-full"
//                     onClick={goNextWeek}
//                   >
//                     ▶
//                   </button>
//                 </div>

//                 <div />
//               </div>

//               <div className="flex justify-between">
//                 {weekDates.map((d, i) => {
//                   const isSelected = d.isSame(selectedDate, "day");
//                   const isToday = d.isSame(moment(), "day");

//                   return (
//                     <div
//                       key={i}
//                       onClick={() => setSelectedDate(d)}
//                       className={`w-[13%] text-center cursor-pointer rounded-md p-2 border 
//                         ${isSelected ? "bg-blue-500 text-white" : "bg-gray-50"} 
//                         ${isToday && !isSelected ? "border-blue-500" : ""}
//                       `}
//                     >
//                       <h6 className="text-[13px] font-semibold">
//                         {d.format("DD MMM")}
//                       </h6>
//                       <h6 className="text-[11px]">
//                         {d.format("ddd").toUpperCase()}
//                       </h6>

//                       {/* dot for hearing */}
//                       {hasHearingOn(d.toDate()) && !isSelected && (
//                         <div className="flex justify-center mt-1">
//                           <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
//                         </div>
//                       )}
//                     </div>
//                   );
//                 })}
//               </div>
//             </div>

//             {/* ----------------------------------------- */}
//             {/* HEARINGS LIST */}
//             {/* ----------------------------------------- */}
//             <div className="p-3 border rounded-md border-slate-100">
//               <h6 className="text-[13px] font-[700] mb-2">
//                 Hearings for {selectedDate.format("DD MMM, ddd")} (
//                 {filteredHearings.length})
//               </h6>

//               {filteredHearings.length === 0 && (
//                 <div className="flex flex-col my-8 w-[80%] mx-auto items-center">
//                   <IoCalendarClearOutline size={40} color="#ddd" />
//                   <h6 className="text-[11px] mt-2">No hearings for this date</h6>
//                 </div>
//               )}

//               {filteredHearings.length > 0 && (
//                 <div className="overflow-y-scroll min-h-[85%] max-h-[85%]">
//                   <div className="flex text-[11px] border mt-2">
//                     <h6 className="w-[40%] p-1 border-r">Case</h6>
//                     <h6 className="w-[30%] p-1 border-r">Date</h6>
//                     <h6 className="w-[30%] p-1">Stage</h6>
//                   </div>

//                   {filteredHearings.map((h, i) => (
//                     <div
//                       key={i}
//                       onClick={() =>
//                         navigator(
//                           `/case/${
//                             h?.case?.case_type?.name == "Civil Court"
//                               ? "civil_court"
//                               : "high_court"
//                           }/detail`,
//                           { state: h?.case }
//                         )
//                       }
//                       className="flex cursor-pointer text-[11px] border-l border-r border-b"
//                     >
//                       <h6 className="w-[40%] truncate p-1 font-[700] underline text-sky-800 border-r">
//                         {h?.client?.name}
//                       </h6>
//                       <h6 className="w-[30%] truncate p-1 border-r">
//                         {moment(h?.next_hearing_date).format("LL")}
//                       </h6>
//                       <h6 className="w-[30%] truncate p-1">
//                         {h?.stage?.name || "---"}
//                       </h6>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>

//     {/* ------------------------------------------------ */}
//     {/* FULL SCREEN GLASS CALENDAR MODAL */}
//     {/* ------------------------------------------------ */}
//     {isCalendarOpen && (
//       <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[999]">

//         <div className="bg-white shadow-xl rounded-xl p-5 w-[90%] md:w-[60%] max-h-[90vh] overflow-hidden relative">

//           {/* Header */}
//           <div className="flex items-center justify-between mb-4">
//             <button
//               className="px-3 py-1 text-xs border rounded-full"
//               onClick={closeCalendar}
//             >
//               Close
//             </button>

//             <div className="flex items-center gap-3">
//               <button
//                 className="px-3 py-1 text-xs border rounded-full"
//                 onClick={goPrevMonth}
//               >
//                 ◀
//               </button>

//               <h2 className="text-lg font-semibold">{calendarMonthLabel}</h2>

//               <button
//                 className="px-3 py-1 text-xs border rounded-full"
//                 onClick={goNextMonth}
//               >
//                 ▶
//               </button>
//             </div>

//             <div />
//           </div>

//           {/* Calendar */}
//           <Calendar
//             onChange={handleCalendarChange}
//             value={calendarDate}
//             tileContent={renderTileContent}
//             className="w-full rounded-lg shadow-md"
//           />
//         </div>
//       </div>
//     )}
//   </div>
// );
// }

// export default Dashboard;













////===>> Old one 100%

// import React, { useState, useEffect } from "react";
// import DashboardMenu from "./DashboardMenu";
// import { useNavigate } from "react-router-dom";
// import { GetHomeDataService } from "../../services/HomeDataServices";
// import { GetActivityService } from "../../services/ActivityServices";

// import { PiSuitcaseSimple } from "react-icons/pi";
// import { TbBriefcaseOff, TbDeviceAirpodsCase, TbMoneybag } from "react-icons/tb";
// import { MdOutlineDriveFileMove, MdOutlineDriveFileMoveRtl, MdOutlinePayment } from "react-icons/md";
// import { FiFileText } from "react-icons/fi";
// import { RiBillLine } from "react-icons/ri";
// import { LuCalendarClock } from "react-icons/lu";
// import { AiOutlineAlert } from "react-icons/ai";
// import { IoCalendarClearOutline } from "react-icons/io5";

// import ReactApexChart from "react-apexcharts";
// import moment from "moment";

// function Dashboard() {
//   const navigator = useNavigate();

//   // ----------------------------------------
//   // MAIN DASHBOARD DATA STATE
//   // ----------------------------------------
//   const [data, setdata] = useState({
//     total_case: 0,
//     total_case_pending: 0,
//     total_case_progress: 0,
//     total_case_completed: 0,
//     total_case_hold: 0,

//     total_opinion_file: 0,
//     total_opinion_file_pending: 0,
//     total_opinion_file_progress: 0,
//     total_opinion_file_completed: 0,
//     total_opinion_file_hold: 0,

//     bills: [],
//     billamount: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     payment: [],
//     paymentamount: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     reminder: [],
//     reminder_active: [],
//     today_case: [],
//     today_office_file: [],
//     today_case_total: 0,
//     today_bills: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     today_payment: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     today_reminder: [],
//     today_reminder_total: 0,
//   });

//   // ----------------------------------------
//   // HEARING LIST STATE (from activity service)
//   // ----------------------------------------
//   const [hearingList, setHearingList] = useState([]);

//   // ----------------------------------------
//   // WEEKLY CALENDAR STATES
//   // ----------------------------------------
//   const [selectedDate, setSelectedDate] = useState(moment().startOf("day"));
//   const [weekDates, setWeekDates] = useState([]);
//   const [filteredHearings, setFilteredHearings] = useState([]);

//   // ----------------------------------------
//   // GENERATE WEEK (Sun → Sat)
//   // ----------------------------------------
//   function generateWeek(date) {
//     let startOfWeek = moment(date).startOf("week"); // Sunday
//     let week = [];

//     for (let i = 0; i < 7; i++) {
//       week.push(moment(startOfWeek).add(i, "days"));
//     }

//     setWeekDates(week);
//   }

//   // ----------------------------------------
//   // WEEK NAVIGATION
//   // ----------------------------------------
//   function goPrevWeek() {
//     const newDate = moment(selectedDate).subtract(7, "days");
//     setSelectedDate(newDate);
//     generateWeek(newDate);
//   }

//   function goNextWeek() {
//     const newDate = moment(selectedDate).add(7, "days");
//     setSelectedDate(newDate);
//     generateWeek(newDate);
//   }

//   // ----------------------------------------
//   // FILTER HEARINGS WHEN SELECTED DATE CHANGES
//   // ----------------------------------------
//   useEffect(() => {
//     const filtered = hearingList.filter((h) =>
//       moment(h.next_hearing_date).isSame(selectedDate, "day")
//     );
//     setFilteredHearings(filtered);
//   }, [selectedDate, hearingList]);

//   // ----------------------------------------
//   // FETCH UPCOMING HEARINGS FROM ACTIVITY SERVICE
//   // ----------------------------------------
//   async function getUpcomingHearings() {
//     try {
//       const res = await GetActivityService("case");
//       const list = res?.data?.datas || [];

//       const today = new Date();
//       today.setHours(0, 0, 0, 0);

//       const upcoming = list.filter(
//         (a) =>
//           a?.next_hearing_date &&
//           new Date(a.next_hearing_date) >= today
//       );

//       upcoming.sort(
//         (a, b) => new Date(a.next_hearing_date) - new Date(b.next_hearing_date)
//       );

//       setHearingList(upcoming);
//     } catch (err) {
//       console.log("Error fetching hearings", err);
//     }
//   }

//   // ----------------------------------------
//   // FETCH DASHBOARD DATA
//   // ----------------------------------------
//   async function getData() {
//     const response = await GetHomeDataService();
//     let d = response?.data?.data;

//     let arr = [
//       { name: "Total", data: [d?.total_case, d?.total_opinion_file] },
//       { name: "Completed", data: [d?.total_case_completed, d?.total_opinion_file_completed] },
//       { name: "Pending", data: [d?.total_case_pending, d?.total_opinion_file_pending] },
//       { name: "Progress", data: [d?.total_case_progress, d?.total_opinion_file_progress] },
//       { name: "Hold", data: [d?.total_case_hold, d?.total_opinion_file_hold] },
//     ];

//     setchartOptions1((prev) => ({
//       ...prev,
//       series: arr,
//       xaxis: { categories: ["Cases", "Opinion File"] },
//     }));

//     setchartOptions2((prev) => ({
//       ...prev,
//       series: [
//         {
//           name: "Amount",
//           data: [
//             d?.today_bills?.totalAmountWithGst,
//             d?.today_payment?.totalAmountWithGst,
//           ],
//         },
//       ],
//     }));

//     setdata(d);
//   }

//   // ----------------------------------------
//   // CHART STATES
//   // ----------------------------------------
//   const [chartOptions1, setchartOptions1] = useState({
//     series: [],
//     chart: { toolbar: { show: false } },
//     plotOptions: {
//       bar: {
//         horizontal: false,
//         columnWidth: "55%",
//         endingShape: "rounded",
//       },
//     },
//     dataLabels: { enabled: false },
//     grid: { show: true, borderColor: "#fafafa" },
//     stroke: { width: 1, curve: "smooth", colors: ["transparent"] },
//     xaxis: { categories: [] },
//     yaxis: { title: { text: "Count" } },
//     fill: { opacity: 1 },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return val;
//         },
//       },
//     },
//     fill: ["#027cd1"],
//   });

//   const [chartOptions2, setchartOptions2] = useState({
//     series: [],
//     chart: { toolbar: { show: false } },
//     plotOptions: {
//       bar: {
//         horizontal: false,
//         columnWidth: "55%",
//         endingShape: "rounded",
//       },
//     },
//     dataLabels: { enabled: false },
//     grid: { show: true, borderColor: "#fafafa" },
//     stroke: { width: 1, curve: "smooth", colors: ["transparent"] },
//     xaxis: { categories: ["Bill Amount", "Payment Collection"] },
//     yaxis: { title: { text: "Count" } },
//     fill: { opacity: 1 },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return val;
//         },
//       },
//     },
//     fill: ["#027cd1"],
//   });

//   // ----------------------------------------
//   // RUN ON MOUNT
//   // ----------------------------------------
//   useEffect(() => {
//     getData();
//     getUpcomingHearings();
//     generateWeek(moment());
//   }, []);

//   let rupeeIndian = Intl.NumberFormat("en-IN", {
//     style: "currency",
//     currency: "INR",
//   });

//     // ----------------------------------------
//   // RADIAL CHART OPTIONS + REVENUE %
//   // ----------------------------------------
//   const chartOptions = {
//     plotOptions: {
//       color: ["#000", "#000"],
//       size: "40%",
//       radialBar: {
//         inverseOrder: false,
//         startAngle: 0,
//         endAngle: 360,
//         offsetX: 0,
//         offsetY: 0,
//         hollow: {
//           margin: 5,
//           size: "50%",
//           color: "#f2f2f2",
//           image: undefined,
//           imageWidth: 150,
//           imageHeight: 150,
//           imageOffsetX: 0,
//           imageOffsetY: 0,
//           imageClipped: true,
//           position: "front",
//         },
//         track: {
//           show: true,
//           background: "#f2f2f2",
//           strokeWidth: "50%",
//           opacity: 1,
//           margin: 5,
//           dropShadow: {
//             enabled: false,
//             top: 0,
//             left: 0,
//             opacity: 1,
//           },
//         },
//         dataLabels: {
//           show: true,
//           name: {
//             show: true,
//             fontSize: "12px",
//             fontWeight: 400,
//             color: "#000",
//           },
//           value: {
//             show: true,
//             fontSize: "14px",
//             fontWeight: 900,
//             offsetY: 1,
//             formatter: function (val) {
//               return val + "%";
//             },
//           },
//         },
//       },
//     },
//     labels: ["Revenue Forecast"],
//     fill: {
//       colors: ["#008ef2"],
//     },
//   };

//   const revenuePercent =
//     data?.billamount?.totalAmountWithGst > 0
//       ? Math.floor(
//           (data?.paymentamount?.totalAmountWithGst /
//             data?.billamount?.totalAmountWithGst) *
//             100
//         )
//       : 0;

//   // ----------------------------------------
//   // JSX
//   // ----------------------------------------
//   return (
//     <div className="md:h-screen md:min-h-screen md:max-h-screen md:overflow-hidden">
//       <div className="md:flex">
//         <DashboardMenu />
//         <div className="md:w-[100%] md:w-[86%] flex md:h-screen md:min-h-screen md:max-h-screen md:overflow-y-scroll">
//           <div className="w-[100%] px-4 pt-5 ">
//             {/* SECTION 1 – TOP STATS */}
//             <div className="grid items-center grid-cols-2 border-t border-b border-l border-r md:grid-cols-6 justify-evenly border-slate-100">
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/case")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <PiSuitcaseSimple
//                     size={23}
//                     className="p-1 mr-2 text-blue-500 bg-blue-200"
//                   />{" "}
//                   Total Case{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.total_case))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/case")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <TbDeviceAirpodsCase
//                     size={23}
//                     className="p-1 mr-2 text-blue-500 bg-blue-200"
//                   />{" "}
//                   Active Case{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(
//                       JSON.stringify(
//                         data?.total_case_pending + data?.total_case_progress
//                       )
//                     )
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/case")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <TbBriefcaseOff
//                     size={23}
//                     className="p-1 mr-2 text-blue-500 bg-blue-200"
//                   />{" "}
//                   InActive Case{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.total_case_hold))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/opinion_files")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <MdOutlineDriveFileMove
//                     size={23}
//                     className="p-1 mr-2 text-yellow-500 bg-yellow-200"
//                   />{" "}
//                   Total Opinion File{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.total_opinion_file))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/opinion_files")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <FiFileText
//                     size={23}
//                     className="p-1 mr-2 text-yellow-500 bg-yellow-200"
//                   />{" "}
//                   Active Opinion File{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(
//                       JSON.stringify(
//                         data?.total_opinion_file_pending +
//                           data?.total_opinion_file_progress
//                       )
//                     )
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/opinion_files")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <MdOutlineDriveFileMoveRtl
//                     size={23}
//                     className="p-1 mr-2 text-yellow-500 bg-yellow-200"
//                   />{" "}
//                   InActive Opinion File{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.total_opinion_file_hold))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-r border-slate-100"
//                 onClick={() => navigator("/bills/list")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <RiBillLine
//                     size={23}
//                     className="p-1 mr-2 text-purple-500 bg-purple-200"
//                   />{" "}
//                   Total Bills{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.bills))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <MdOutlinePayment
//                     size={23}
//                     className="p-1 mr-2 text-purple-500 bg-purple-200"
//                   />{" "}
//                   Total Payment{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.payment))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <AiOutlineAlert
//                     size={23}
//                     className="p-1 mr-2 text-red-500 bg-red-200"
//                   />{" "}
//                   Total Reminder{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.reminder))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <LuCalendarClock
//                     size={23}
//                     className="p-1 mr-2 text-red-500 bg-red-200"
//                   />{" "}
//                   Active Reminder{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.reminder_active))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>

//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <TbMoneybag
//                     size={23}
//                     className="p-1 mr-2 text-green-500 bg-green-200"
//                   />{" "}
//                   Total Revenue{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(
//                       JSON.stringify(
//                         data?.billamount?.totalAmountWithGst
//                       )
//                     )
//                     ?.split(".")[0]
//                     .slice(0)}
//                 </h6>
//               </div>
//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <TbMoneybag
//                     size={23}
//                     className="p-1 mr-2 text-green-500 bg-green-200"
//                   />{" "}
//                   Collect Revenue{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(
//                       JSON.stringify(
//                         data?.paymentamount?.totalAmountWithGst
//                       )
//                     )
//                     ?.split(".")[0]
//                     .slice(0)}
//                 </h6>
//               </div>
//             </div>
//             {/* SECTION 4 – WEEKLY CALENDAR + HEARINGS FOR SELECTED DATE */}
//             <div className="mt-5 md:mt-3">
//               {/* Weekly Calendar Bar */}
//               <div className="p-3 mb-3 border rounded-md border-slate-100">
//                 <div className="flex items-center justify-between mb-3">
//                   <button
//                     className="px-3 py-1 text-xs border rounded-full"
//                     onClick={() => {
//                       const today = moment();
//                       setSelectedDate(today);
//                       generateWeek(today);
//                     }}
//                   >
//                     Today
//                   </button>

//                   <div className="flex items-center space-x-2">
//                     <button
//                       className="px-2 py-1 text-xs border rounded-full"
//                       onClick={goPrevWeek}
//                     >
//                       ◀
//                     </button>

//                     <span className="text-sm font-semibold">
//                       {selectedDate.format("MMM YYYY")}
//                     </span>

//                     <button
//                       className="px-2 py-1 text-xs border rounded-full"
//                       onClick={goNextWeek}
//                     >
//                       ▶
//                     </button>
//                   </div>

//                   <div />
//                 </div>

//                 <div className="flex justify-between">
//                   {weekDates.map((d, i) => {
//                     const isSelected = d.isSame(selectedDate, "day");
//                     const isToday = d.isSame(moment(), "day");

//                     return (
//                       <div
//                         key={i}
//                         onClick={() => setSelectedDate(d)}
//                         className={`w-[13%] text-center cursor-pointer rounded-md p-2 border ${
//                           isSelected
//                             ? "bg-blue-500 text-white"
//                             : "bg-gray-50"
//                         } ${
//                           isToday && !isSelected
//                             ? "border-blue-500"
//                             : ""
//                         }`}
//                       >
//                         <h6 className="text-[13px] font-semibold">
//                           {d.format("DD MMM")}
//                         </h6>
//                         <h6 className="text-[11px]">
//                           {d.format("ddd").toUpperCase()}
//                         </h6>
//                       </div>
//                     );
//                   })}
//                 </div>
//               </div>

//               {/* Hearings for Selected Date */}
//               <div className="p-3 border rounded-md border-slate-100">
//                 <h6 className="text-[13px] font-[700] mb-2">
//                   Hearings for {selectedDate.format("DD MMM, ddd")} (
//                   {filteredHearings.length})
//                 </h6>

//                 {filteredHearings.length === 0 && (
//                   <div className="flex flex-col my-8 w-[80%] mx-auto items-center">
//                     <IoCalendarClearOutline size={40} color="#ddd" />
//                     <h6 className="text-[11px] mt-2">
//                       No hearings for this date
//                     </h6>
//                   </div>
//                 )}

//                 {filteredHearings.length > 0 && (
//                   <div className="overflow-y-scroll min-h-[85%] max-h-[85%]">
//                     <div className="flex text-[11px] border mt-2">
//                       <h6 className="w-[40%] p-1 border-r">Case</h6>
//                       <h6 className="w-[30%] p-1 border-r">Date</h6>
//                       <h6 className="w-[30%] p-1">Stage</h6>
//                     </div>

//                     {filteredHearings.map((h, i) => (
//                       <div
//                         key={i}
//                         onClick={() =>
//                           navigator(
//                             `/case/${
//                               h?.case?.case_type?.name ==
//                               "Civil Court"
//                                 ? "civil_court"
//                                 : "high_court"
//                             }/detail`,
//                             { state: h?.case }
//                           )
//                         }
//                         className="flex cursor-pointer text-[11px] border-l border-r border-b"
//                       >
//                         <h6 className="w-[40%] truncate p-1 font-[700] underline text-sky-800 border-r">
//                           {h?.client?.name}
//                         </h6>
//                         <h6 className="w-[30%] truncate p-1 border-r">
//                           {moment(h?.next_hearing_date).format("LL")}
//                         </h6>
//                         <h6 className="w-[30%] truncate p-1">
//                           {h?.stage?.name || "---"}
//                         </h6>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default Dashboard;








//==========>>> Old one with ccalender updsting



// import React, { useState, useEffect } from "react";
// import DashboardMenu from "./DashboardMenu";
// import { useNavigate } from "react-router-dom";
// import { GetHomeDataService } from "../../services/HomeDataServices";
// import { GetActivityService } from "../../services/ActivityServices";

// import { PiSuitcaseSimple } from "react-icons/pi";
// import { TbBriefcaseOff, TbDeviceAirpodsCase, TbMoneybag } from "react-icons/tb";
// import { MdOutlineDriveFileMove, MdOutlineDriveFileMoveRtl, MdOutlinePayment } from "react-icons/md";
// import { FiFileText } from "react-icons/fi";
// import { RiBillLine } from "react-icons/ri";
// import { LuCalendarClock } from "react-icons/lu";
// import { AiOutlineAlert } from "react-icons/ai";
// import { IoCalendarClearOutline } from "react-icons/io5";

// import ReactApexChart from "react-apexcharts";
// import moment from "moment";

// function Dashboard() {
//   const navigator = useNavigate();

//   // ----------------------------------------
//   // MAIN DASHBOARD DATA STATE
//   // ----------------------------------------
//   const [data, setdata] = useState({
//     total_case: 0,
//     total_case_pending: 0,
//     total_case_progress: 0,
//     total_case_completed: 0,
//     total_case_hold: 0,

//     total_opinion_file: 0,
//     total_opinion_file_pending: 0,
//     total_opinion_file_progress: 0,
//     total_opinion_file_completed: 0,
//     total_opinion_file_hold: 0,

//     bills: [],
//     billamount: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     payment: [],
//     paymentamount: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     reminder: [],
//     reminder_active: [],
//     today_case: [],
//     today_office_file: [],
//     today_case_total: 0,
//     today_bills: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     today_payment: { totalAmountWithGst: 0, totalAmountWithoutGst: 0 },
//     today_reminder: [],
//     today_reminder_total: 0,
//   });

//   // ----------------------------------------
//   // HEARING LIST STATE (from activity service)
//   // ----------------------------------------
//   const [hearingList, setHearingList] = useState([]);

//   // ----------------------------------------
//   // WEEKLY CALENDAR STATES
//   // ----------------------------------------
//   const [selectedDate, setSelectedDate] = useState(moment().startOf("day"));
//   const [weekDates, setWeekDates] = useState([]);
//   const [filteredHearings, setFilteredHearings] = useState([]);

//   // ----------------------------------------
//   // GENERATE WEEK (Sun → Sat)
//   // ----------------------------------------
//   function generateWeek(date) {
//     let startOfWeek = moment(date).startOf("week"); // Sunday
//     let week = [];

//     for (let i = 0; i < 7; i++) {
//       week.push(moment(startOfWeek).add(i, "days"));
//     }

//     setWeekDates(week);
//   }

//   // ----------------------------------------
//   // WEEK NAVIGATION
//   // ----------------------------------------
//   function goPrevWeek() {
//     const newDate = moment(selectedDate).subtract(7, "days");
//     setSelectedDate(newDate);
//     generateWeek(newDate);
//   }

//   function goNextWeek() {
//     const newDate = moment(selectedDate).add(7, "days");
//     setSelectedDate(newDate);
//     generateWeek(newDate);
//   }

//   // ----------------------------------------
//   // FILTER HEARINGS WHEN SELECTED DATE CHANGES
//   // ----------------------------------------
//   useEffect(() => {
//     const filtered = hearingList.filter((h) =>
//       moment(h.next_hearing_date).isSame(selectedDate, "day")
//     );
//     setFilteredHearings(filtered);
//   }, [selectedDate, hearingList]);

//   // ----------------------------------------
//   // FETCH UPCOMING HEARINGS FROM ACTIVITY SERVICE
//   // ----------------------------------------
//   async function getUpcomingHearings() {
//     try {
//       const res = await GetActivityService("case");
//       const list = res?.data?.datas || [];

//       const today = new Date();
//       today.setHours(0, 0, 0, 0);

//       const upcoming = list.filter(
//         (a) =>
//           a?.next_hearing_date &&
//           new Date(a.next_hearing_date) >= today
//       );

//       upcoming.sort(
//         (a, b) => new Date(a.next_hearing_date) - new Date(b.next_hearing_date)
//       );

//       setHearingList(upcoming);
//     } catch (err) {
//       console.log("Error fetching hearings", err);
//     }
//   }

//   // ----------------------------------------
//   // FETCH DASHBOARD DATA
//   // ----------------------------------------
//   async function getData() {
//     const response = await GetHomeDataService();
//     let d = response?.data?.data;

//     let arr = [
//       { name: "Total", data: [d?.total_case, d?.total_opinion_file] },
//       { name: "Completed", data: [d?.total_case_completed, d?.total_opinion_file_completed] },
//       { name: "Pending", data: [d?.total_case_pending, d?.total_opinion_file_pending] },
//       { name: "Progress", data: [d?.total_case_progress, d?.total_opinion_file_progress] },
//       { name: "Hold", data: [d?.total_case_hold, d?.total_opinion_file_hold] },
//     ];

//     setchartOptions1((prev) => ({
//       ...prev,
//       series: arr,
//       xaxis: { categories: ["Cases", "Opinion File"] },
//     }));

//     setchartOptions2((prev) => ({
//       ...prev,
//       series: [
//         {
//           name: "Amount",
//           data: [
//             d?.today_bills?.totalAmountWithGst,
//             d?.today_payment?.totalAmountWithGst,
//           ],
//         },
//       ],
//     }));

//     setdata(d);
//   }

//   // ----------------------------------------
//   // CHART STATES
//   // ----------------------------------------
//   const [chartOptions1, setchartOptions1] = useState({
//     series: [],
//     chart: { toolbar: { show: false } },
//     plotOptions: {
//       bar: {
//         horizontal: false,
//         columnWidth: "55%",
//         endingShape: "rounded",
//       },
//     },
//     dataLabels: { enabled: false },
//     grid: { show: true, borderColor: "#fafafa" },
//     stroke: { width: 1, curve: "smooth", colors: ["transparent"] },
//     xaxis: { categories: [] },
//     yaxis: { title: { text: "Count" } },
//     fill: { opacity: 1 },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return val;
//         },
//       },
//     },
//     fill: ["#027cd1"],
//   });

//   const [chartOptions2, setchartOptions2] = useState({
//     series: [],
//     chart: { toolbar: { show: false } },
//     plotOptions: {
//       bar: {
//         horizontal: false,
//         columnWidth: "55%",
//         endingShape: "rounded",
//       },
//     },
//     dataLabels: { enabled: false },
//     grid: { show: true, borderColor: "#fafafa" },
//     stroke: { width: 1, curve: "smooth", colors: ["transparent"] },
//     xaxis: { categories: ["Bill Amount", "Payment Collection"] },
//     yaxis: { title: { text: "Count" } },
//     fill: { opacity: 1 },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return val;
//         },
//       },
//     },
//     fill: ["#027cd1"],
//   });

//   // ----------------------------------------
//   // RUN ON MOUNT
//   // ----------------------------------------
//   useEffect(() => {
//     getData();
//     getUpcomingHearings();
//     generateWeek(moment());
//   }, []);

//   let rupeeIndian = Intl.NumberFormat("en-IN", {
//     style: "currency",
//     currency: "INR",
//   });

//     // ----------------------------------------
//   // RADIAL CHART OPTIONS + REVENUE %
//   // ----------------------------------------
//   const chartOptions = {
//     plotOptions: {
//       color: ["#000", "#000"],
//       size: "40%",
//       radialBar: {
//         inverseOrder: false,
//         startAngle: 0,
//         endAngle: 360,
//         offsetX: 0,
//         offsetY: 0,
//         hollow: {
//           margin: 5,
//           size: "50%",
//           color: "#f2f2f2",
//           image: undefined,
//           imageWidth: 150,
//           imageHeight: 150,
//           imageOffsetX: 0,
//           imageOffsetY: 0,
//           imageClipped: true,
//           position: "front",
//         },
//         track: {
//           show: true,
//           background: "#f2f2f2",
//           strokeWidth: "50%",
//           opacity: 1,
//           margin: 5,
//           dropShadow: {
//             enabled: false,
//             top: 0,
//             left: 0,
//             opacity: 1,
//           },
//         },
//         dataLabels: {
//           show: true,
//           name: {
//             show: true,
//             fontSize: "12px",
//             fontWeight: 400,
//             color: "#000",
//           },
//           value: {
//             show: true,
//             fontSize: "14px",
//             fontWeight: 900,
//             offsetY: 1,
//             formatter: function (val) {
//               return val + "%";
//             },
//           },
//         },
//       },
//     },
//     labels: ["Revenue Forecast"],
//     fill: {
//       colors: ["#008ef2"],
//     },
//   };

//   const revenuePercent =
//     data?.billamount?.totalAmountWithGst > 0
//       ? Math.floor(
//           (data?.paymentamount?.totalAmountWithGst /
//             data?.billamount?.totalAmountWithGst) *
//             100
//         )
//       : 0;

//   // ----------------------------------------
//   // JSX
//   // ----------------------------------------
//   return (
//     <div className="md:h-screen md:min-h-screen md:max-h-screen md:overflow-hidden">
//       <div className="md:flex">
//         <DashboardMenu />
//         <div className="md:w-[100%] md:w-[86%] flex md:h-screen md:min-h-screen md:max-h-screen md:overflow-y-scroll">
//           <div className="w-[100%] px-4 pt-5 ">
//             {/* SECTION 1 – TOP STATS */}
//             <div className="grid items-center grid-cols-2 border-t border-b border-l border-r md:grid-cols-6 justify-evenly border-slate-100">
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/case")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <PiSuitcaseSimple
//                     size={23}
//                     className="p-1 mr-2 text-blue-500 bg-blue-200"
//                   />{" "}
//                   Total Case{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.total_case))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/case")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <TbDeviceAirpodsCase
//                     size={23}
//                     className="p-1 mr-2 text-blue-500 bg-blue-200"
//                   />{" "}
//                   Active Case{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(
//                       JSON.stringify(
//                         data?.total_case_pending + data?.total_case_progress
//                       )
//                     )
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/case")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <TbBriefcaseOff
//                     size={23}
//                     className="p-1 mr-2 text-blue-500 bg-blue-200"
//                   />{" "}
//                   InActive Case{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.total_case_hold))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/opinion_files")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <MdOutlineDriveFileMove
//                     size={23}
//                     className="p-1 mr-2 text-yellow-500 bg-yellow-200"
//                   />{" "}
//                   Total Opinion File{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.total_opinion_file))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/opinion_files")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <FiFileText
//                     size={23}
//                     className="p-1 mr-2 text-yellow-500 bg-yellow-200"
//                   />{" "}
//                   Active Opinion File{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(
//                       JSON.stringify(
//                         data?.total_opinion_file_pending +
//                           data?.total_opinion_file_progress
//                       )
//                     )
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-b border-r border-slate-100"
//                 onClick={() => navigator("/opinion_files")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <MdOutlineDriveFileMoveRtl
//                     size={23}
//                     className="p-1 mr-2 text-yellow-500 bg-yellow-200"
//                   />{" "}
//                   InActive Opinion File{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.total_opinion_file_hold))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div
//                 className="w-full px-2 py-2 border-r border-slate-100"
//                 onClick={() => navigator("/bills/list")}
//               >
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <RiBillLine
//                     size={23}
//                     className="p-1 mr-2 text-purple-500 bg-purple-200"
//                   />{" "}
//                   Total Bills{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.bills))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <MdOutlinePayment
//                     size={23}
//                     className="p-1 mr-2 text-purple-500 bg-purple-200"
//                   />{" "}
//                   Total Payment{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.payment))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <AiOutlineAlert
//                     size={23}
//                     className="p-1 mr-2 text-red-500 bg-red-200"
//                   />{" "}
//                   Total Reminder{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.reminder))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>
//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <LuCalendarClock
//                     size={23}
//                     className="p-1 mr-2 text-red-500 bg-red-200"
//                   />{" "}
//                   Active Reminder{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(JSON.stringify(data?.reminder_active))
//                     ?.split(".")[0]
//                     .slice(1)}
//                 </h6>
//               </div>

//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <TbMoneybag
//                     size={23}
//                     className="p-1 mr-2 text-green-500 bg-green-200"
//                   />{" "}
//                   Total Revenue{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(
//                       JSON.stringify(
//                         data?.billamount?.totalAmountWithGst
//                       )
//                     )
//                     ?.split(".")[0]
//                     .slice(0)}
//                 </h6>
//               </div>
//               <div className="w-full px-2 py-2 border-r border-slate-100">
//                 <h6 className="text-[12px] text-slate-700 font-[600] flex items-center">
//                   <TbMoneybag
//                     size={23}
//                     className="p-1 mr-2 text-green-500 bg-green-200"
//                   />{" "}
//                   Collect Revenue{" "}
//                 </h6>
//                 <h6 className="text-[14px] mt-1">
//                   {rupeeIndian
//                     ?.format(
//                       JSON.stringify(
//                         data?.paymentamount?.totalAmountWithGst
//                       )
//                     )
//                     ?.split(".")[0]
//                     .slice(0)}
//                 </h6>
//               </div>
//             </div>

//             {/* SECTION 2 – REVENUE + CASE/OPINION CHART */}
//             <div className="mt-5 md:flex md:mt-2">
//               <div className=" md:w-[29.5%] md:mr-[0.5%] border border-slate-100 p-5 px-3 py-2">
//                 <h6 className="text-[13px] font-[700]">
//                   Revenue Generated
//                 </h6>

//                 <div className="flex ">
//                   <div className="-mt-5 -ml-14">
//                     <ReactApexChart
//                       options={chartOptions}
//                       series={[revenuePercent]}
//                       type="radialBar"
//                       width={240}
//                       height={240}
//                     />
//                   </div>
//                   <div className="w-[100%] -ml-[40px] -mt-8 -ml-2">
//                     <div className="relative w-full mt-16">
//                       <h6 className="text-[11px] font-[600]">
//                         Bill Amount{" "}
//                       </h6>
//                       <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-2 dark:bg-gray-700">
//                         <div
//                           className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none w-[100%]`}
//                           style={{
//                             width:
//                               data?.billamount?.totalAmountWithGst > 0
//                                 ? "100%"
//                                 : "0%",
//                           }}
//                         >
//                           {data?.billamount?.totalAmountWithGst > 0
//                             ? "100%"
//                             : "0%"}
//                         </div>
//                       </div>
//                       <h6 className="text-[11px] font-[700] absolute top-0 right-0">
//                         {
//                           rupeeIndian
//                             ?.format(
//                               JSON.stringify(
//                                 data?.billamount?.totalAmountWithGst
//                               )
//                             )
//                             ?.split(".")[0]
//                         }
//                       </h6>
//                     </div>

//                     <div className="relative w-full mt-4 ml-0">
//                       <h6 className="text-[11px] font-[600]">
//                         Payment Collected{" "}
//                       </h6>
//                       <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-4 dark:bg-gray-700">
//                         <div
//                           className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none`}
//                           style={{
//                             width:
//                               data?.paymentamount?.totalAmountWithGst >=
//                               data?.billamount?.totalAmountWithGst
//                                 ? "100%"
//                                 : `${
//                                     data?.billamount
//                                       ?.totalAmountWithGst > 0
//                                       ? Math.round(
//                                           (data?.paymentamount
//                                             ?.totalAmountWithGst /
//                                             data?.billamount
//                                               ?.totalAmountWithGst) *
//                                             100
//                                         )
//                                       : 0
//                                   }%`,
//                           }}
//                         >
//                           {data?.paymentamount?.totalAmountWithGst > 0 &&
//                           data?.billamount?.totalAmountWithGst > 0
//                             ? Math.round(
//                                 (data?.paymentamount
//                                   ?.totalAmountWithGst /
//                                   data?.billamount
//                                     ?.totalAmountWithGst) *
//                                   100
//                               )
//                             : 0}
//                           %
//                         </div>
//                       </div>
//                       <h6 className="text-[11px] font-[700] absolute top-0 right-0">
//                         {
//                           rupeeIndian
//                             ?.format(
//                               JSON.stringify(
//                                 data?.paymentamount?.totalAmountWithGst
//                               )
//                             )
//                             ?.split(".")[0]
//                         }
//                       </h6>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className={`border mt-2 md:mt-0 md:w-[69.5%] md:ml-[0.5%]   border-slate-100 px-3 py-2 relative`}>
//                 <h1 className="text-[13px] font-[700]">
//                   Case / Opinion File
//                 </h1>
//                 <ReactApexChart
//                   options={chartOptions1}
//                   series={chartOptions1.series}
//                   type="bar"
//                   width={"100%"}
//                   height={220}
//                 />
//               </div>
//             </div>

//             {/* SECTION 3 – TODAY CASE / REMINDER / TODAY BILL */}
//             <div className="mt-5 md:flex md:mt-2">
//               {/* Today Case / Opinion */}
//               <div className="border md:w-[33%] border-slate-100 p-2">
//                 <h6 className="text-[13px] font-[700]">
//                   Today Case / Opinion File Register (
//                   {data?.today_case_total})
//                 </h6>

//                 {data?.today_case_total === 0 && (
//                   <div className="flex flex-col my-8 w-[80%] mx-auto items-center">
//                     <IoCalendarClearOutline size={40} color="#ddd" />
//                     <h6 className="text-[11px] mt-2">
//                       No Case / Opinion File found for today
//                     </h6>
//                     <h6 className="text-[11px] text-center mt-1">
//                       We found that you have not added any case or
//                       opinion file for today session.
//                     </h6>
//                   </div>
//                 )}

//                 {(data?.today_case?.length > 0 ||
//                   data?.today_office_file?.length > 0) && (
//                   <div className="overflow-y-scroll min-h-[85%] max-h-[85%]">
//                     <div className="flex text-[11px] border mt-2">
//                       <h6 className="w-[25%] p-1 border-r">Client</h6>
//                       <h6 className="w-[25%] p-1 border-r">
//                         Office File No / Case No
//                       </h6>
//                       <h6 className="w-[25%] p-1 border-r">
//                         Case Work
//                       </h6>
//                       <h6 className="w-[25%] p-1 ">Status</h6>
//                     </div>

//                     {data?.today_case?.map((c, i) => (
//                       <div
//                         key={i}
//                         onClick={() =>
//                           navigator("/case/civil_court/detail", {
//                             state: c,
//                           })
//                         }
//                         className="flex cursor-pointer text-[11px] border-l border-r border-b"
//                       >
//                         <h6 className="w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r">
//                           {c?.client?.name}
//                         </h6>
//                         <h6 className="w-[25%] truncate p-1 border-r">
//                           {c?.office_file_no} / {c?.case_no}
//                         </h6>
//                         <h6 className="w-[25%] truncate p-1 border-r">
//                           {c?.case_details}
//                         </h6>
//                         <h6 className="w-[25%] truncate p-1 ">
//                           {c?.status}
//                         </h6>
//                       </div>
//                     ))}

//                     {data?.today_office_file?.map((c, i) => (
//                       <div
//                         key={i}
//                         onClick={() =>
//                           navigator("/opinion_files/list/detail", {
//                             state: c,
//                           })
//                         }
//                         className="flex cursor-pointer text-[11px] border-l border-r border-b"
//                       >
//                         <h6 className="w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r">
//                           {c?.client?.name}
//                         </h6>
//                         <h6 className="w-[25%] truncate p-1 border-r">
//                           {c?.office_file_no}
//                         </h6>
//                         <h6 className="w-[25%] truncate p-1 border-r">
//                           {c?.work_details}
//                         </h6>
//                         <h6 className="w-[25%] truncate p-1 ">
//                           {c?.status}
//                         </h6>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>

//               {/* Upcoming Reminder */}
//               <div className="border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2">
//                 <h6 className="text-[13px] font-[700]">
//                   Upcoming Reminder ({data?.today_reminder_total})
//                 </h6>

//                 {data?.today_reminder_total === 0 && (
//                   <div className="flex flex-col my-8 w-[80%] mx-auto items-center">
//                     <IoCalendarClearOutline size={40} color="#ddd" />
//                     <h6 className="text-[11px] mt-2">
//                       No Reminder found for today
//                     </h6>
//                     <h6 className="text-[11px] text-center">
//                       We found that you have not added any reminder for
//                       today session.
//                     </h6>
//                   </div>
//                 )}

//                 {data?.today_reminder_total > 0 && (
//                   <div className="overflow-y-scroll min-h-[85%] max-h-[85%]">
//                     <div className="flex text-[11px] border mt-2">
//                       <h6 className="w-[50%] p-1 border-r">
//                         Case Info
//                       </h6>
//                       <h6 className="w-[25%] p-1 border-r">Title</h6>
//                       <h6 className="w-[25%] p-1 ">Summary</h6>
//                     </div>

//                     {data?.today_reminder?.map((r, i) => (
//                       <div
//                         key={i}
//                         onClick={() =>
//                           r?.type === "case"
//                             ? navigator(
//                                 `/case/${
//                                   r?.case?.case_type?.name ==
//                                   "Civil Court"
//                                     ? "civil_court"
//                                     : "high_court"
//                                 }/detail`,
//                                 { state: r?.case }
//                               )
//                             : navigator(
//                                 "/opinion_files/list/detail",
//                                 { state: r?.opinion_file }
//                               )
//                         }
//                         className="flex cursor-pointer text-[11px] border-l border-r border-b"
//                       >
//                         <h6 className="w-[50%] truncate p-1 font-[700] text-sky-800 border-r">
//                           {" "}
//                           Type :{" "}
//                           <span className="underline">
//                             {r?.type}
//                           </span>
//                           {r?.type === "opinion_file" && (
//                             <div className="break-all">
//                               <span>
//                                 Opinion Info :{" "}
//                                 {r?.opinion_file?.office_file_no} /{" "}
//                                 {r?.opinion_file?.work_details} <br />
//                                 Client Info :{" "}
//                                 {r?.opinion_file?.client?.name} <br />
//                                 {r?.opinion_file?.client?.mobile}
//                               </span>
//                             </div>
//                           )}
//                           {r?.type === "case" && (
//                             <div className="break-all">
//                               <span>
//                                 Case Info :{" "}
//                                 {r?.case?.office_file_no} /{" "}
//                                 {r?.case?.case_details} <br />
//                                 Client Info : {r?.case?.client?.name}{" "}
//                                 <br />
//                                 {r?.case?.client?.mobile}
//                               </span>
//                             </div>
//                           )}
//                         </h6>
//                         <h6 className="w-[25%] truncate p-1 font-[700] border-r">
//                           {r?.title}
//                         </h6>
//                         <h6 className="w-[25%] truncate p-1 ">
//                           {r?.description}{" "}
//                         </h6>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>

//               {/* Today Bill vs Collection */}
//               <div className="border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2">
//                 <h6 className="text-[13px] font-[700]">
//                   Today Bill Raised vs Collection
//                 </h6>

//                 {data?.today_bills?.totalAmountWithGst === 0 &&
//                   data?.today_payment?.totalAmountWithGst === 0 && (
//                     <div className="flex flex-col my-8 w-[80%] mx-auto items-center">
//                       <IoCalendarClearOutline size={40} color="#ddd" />
//                       <h6 className="text-[11px] mt-2">
//                         No Bill or Payment Collection Made
//                       </h6>
//                       <h6 className="text-[11px] text-center mt-1">
//                         We found that you have not created any bill or
//                         payment collection made for today.
//                       </h6>
//                     </div>
//                   )}

//                 {(data?.today_bills?.totalAmountWithGst > 0 ||
//                   data?.today_payment?.totalAmountWithGst > 0) && (
//                   <div className="">
//                     <ReactApexChart
//                       options={chartOptions2}
//                       series={chartOptions2?.series}
//                       type="bar"
//                       width={"100%"}
//                       height={150}
//                     />
//                   </div>
//                 )}
//               </div>
//             </div>

//             {/* SECTION 4 – WEEKLY CALENDAR + HEARINGS FOR SELECTED DATE */}
//             <div className="mt-5 md:mt-3">
//               {/* Weekly Calendar Bar */}
//               <div className="p-3 mb-3 border rounded-md border-slate-100">
//                 <div className="flex items-center justify-between mb-3">
//                   <button
//                     className="px-3 py-1 text-xs border rounded-full"
//                     onClick={() => {
//                       const today = moment();
//                       setSelectedDate(today);
//                       generateWeek(today);
//                     }}
//                   >
//                     Today
//                   </button>

//                   <div className="flex items-center space-x-2">
//                     <button
//                       className="px-2 py-1 text-xs border rounded-full"
//                       onClick={goPrevWeek}
//                     >
//                       ◀
//                     </button>

//                     <span className="text-sm font-semibold">
//                       {selectedDate.format("MMM YYYY")}
//                     </span>

//                     <button
//                       className="px-2 py-1 text-xs border rounded-full"
//                       onClick={goNextWeek}
//                     >
//                       ▶
//                     </button>
//                   </div>

//                   <div />
//                 </div>

//                 <div className="flex justify-between">
//                   {weekDates.map((d, i) => {
//                     const isSelected = d.isSame(selectedDate, "day");
//                     const isToday = d.isSame(moment(), "day");

//                     return (
//                       <div
//                         key={i}
//                         onClick={() => setSelectedDate(d)}
//                         className={`w-[13%] text-center cursor-pointer rounded-md p-2 border ${
//                           isSelected
//                             ? "bg-blue-500 text-white"
//                             : "bg-gray-50"
//                         } ${
//                           isToday && !isSelected
//                             ? "border-blue-500"
//                             : ""
//                         }`}
//                       >
//                         <h6 className="text-[13px] font-semibold">
//                           {d.format("DD MMM")}
//                         </h6>
//                         <h6 className="text-[11px]">
//                           {d.format("ddd").toUpperCase()}
//                         </h6>
//                       </div>
//                     );
//                   })}
//                 </div>
//               </div>

//               {/* Hearings for Selected Date */}
//               <div className="p-3 border rounded-md border-slate-100">
//                 <h6 className="text-[13px] font-[700] mb-2">
//                   Hearings for {selectedDate.format("DD MMM, ddd")} (
//                   {filteredHearings.length})
//                 </h6>

//                 {filteredHearings.length === 0 && (
//                   <div className="flex flex-col my-8 w-[80%] mx-auto items-center">
//                     <IoCalendarClearOutline size={40} color="#ddd" />
//                     <h6 className="text-[11px] mt-2">
//                       No hearings for this date
//                     </h6>
//                   </div>
//                 )}

//                 {filteredHearings.length > 0 && (
//                   <div className="overflow-y-scroll min-h-[85%] max-h-[85%]">
//                     <div className="flex text-[11px] border mt-2">
//                       <h6 className="w-[40%] p-1 border-r">Case</h6>
//                       <h6 className="w-[30%] p-1 border-r">Date</h6>
//                       <h6 className="w-[30%] p-1">Stage</h6>
//                     </div>

//                     {filteredHearings.map((h, i) => (
//                       <div
//                         key={i}
//                         onClick={() =>
//                           navigator(
//                             `/case/${
//                               h?.case?.case_type?.name ==
//                               "Civil Court"
//                                 ? "civil_court"
//                                 : "high_court"
//                             }/detail`,
//                             { state: h?.case }
//                           )
//                         }
//                         className="flex cursor-pointer text-[11px] border-l border-r border-b"
//                       >
//                         <h6 className="w-[40%] truncate p-1 font-[700] underline text-sky-800 border-r">
//                           {h?.client?.name}
//                         </h6>
//                         <h6 className="w-[30%] truncate p-1 border-r">
//                           {moment(h?.next_hearing_date).format("LL")}
//                         </h6>
//                         <h6 className="w-[30%] truncate p-1">
//                           {h?.stage?.name || "---"}
//                         </h6>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default Dashboard;









//===========>> Old one good above calender updating



// import React,{useState,useEffect} from 'react'
// import {RiCustomerService2Line} from 'react-icons/ri';
// import DashboardMenu from './DashboardMenu'
// // import { GetTodayLeadReminderService, getInsentiveTargetAchieved, getTargetAchieved } from '../../services/LeadServices';
// import { BiLandscape } from 'react-icons/bi';
// import { useNavigate } from 'react-router-dom';
// import { GetHomeDataService } from '../../services/HomeDataServices';
// import { GetActivityService } from '../../services/ActivityServices';
// import { PiSuitcaseSimple } from "react-icons/pi";
// import { TbBriefcaseOff } from "react-icons/tb";
// import { TbDeviceAirpodsCase } from "react-icons/tb";
// import { MdOutlineDriveFileMove,MdOutlineDriveFileMoveRtl,MdOutlinePayment } from "react-icons/md";
// import { FiFileText } from "react-icons/fi";
// import { RiBillLine } from "react-icons/ri";
// import { LuCalendarClock } from "react-icons/lu";
// import { AiOutlineAlert } from "react-icons/ai";
// import { TbMoneybag } from "react-icons/tb";
// import ReactApexChart from 'react-apexcharts';
// import { IoCalendarClearOutline } from "react-icons/io5";
// import moment from 'moment';

// function Dashboard() {


//   const navigator = useNavigate()
//   const [data,setdata] = useState({
//       total_case:0,
//       total_case_pending:0,
//       total_case_progress:0,
//       total_case_completed:0,
//       total_case_hold:0,

//       total_opinion_file:0,
//       total_opinion_file_pending:0,
//       total_opinion_file_progress:0,
//       total_opinion_file_completed:0,
//       total_opinion_file_hold:0,

//       bills:[],
//       billamount:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       payment:[],
//       paymentamount:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       reminder:[],
//       reminder_active:[],
//       today_case:[],
//       today_office_file:[],
//       today_case_total:0,
//       today_bills:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       today_payment:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       today_reminder:[],
//       today_reminder_total:0

//   })
  
//   useEffect(()=>{
//     getData();
//     getUpcomingHearings();
//   },[])

//   const chartOptions = {
//     plotOptions: {
//       color:['#000','#000'],
//       size:'40%',
//       radialBar: {
//           inverseOrder: false,
//           startAngle: 0,
//           endAngle: 360,
//           offsetX: 0,
//           offsetY: 0,
//           hollow: {
//               margin: 5,
//               size: '50%',
//               color: '#f2f2f2',
//               image: undefined,
//               imageWidth: 150,
//               imageHeight: 150,
//               imageOffsetX: 0,
//               imageOffsetY: 0,
//               imageClipped: true,
//               position: 'front',
             
//           },
//           track: {
//               show: true,
//               startAngle: undefined,
//               endAngle: undefined,
//               background: '#f2f2f2',
//               strokeWidth: '50%',
//               opacity: 1,
//               margin: 5, 
//               dropShadow: {
//                   enabled: false,
//                   top: 0,
//                   left: 0,
//                   opacity: 1
//               }
//           },
//           dataLabels: {
//               show: true,
//               name: {
//                   show: true,
//                   fontSize: '12px',
//                   fontFamily: undefined,
//                   fontWeight: 400,
//                   color: undefined,
//                   color:'#000',
//                 },
//                 value: {
//                   show: true,
//                   fontSize: '14px',
//                   fontFamily: undefined,
//                   fontWeight: 900,
//                   color: undefined,
//                   offsetY: 1,
//                   formatter: function (val) {
//                     return val + '%'
//                   }
//                 },
                
//           },
          
//       },
//     },
//     labels: ['Revenue Forecast'],
//     fill:{
//       colors: ['#008ef2'],
//     }
//   }

//   const [chartOptions1,setchartOptions1] = useState({
//     series: [],

//     chart: {
//       toolbar: {
//         show: false
//       }
//     },
//     plotOptions: {
      
//       bar: {
//         horizontal: false,
//         columnWidth: '55%',
//         endingShape: 'rounded',
//         dataLabels: {
//           position: 'top', // top, center, bottom
//         },
        
//       },
//     },
//     dataLabels: {
//       enabled: false
//     },
//     grid:{
//       show: true,
//       borderColor: '#fafafa',
//     },
  
//     stroke: {
//       // show: true,
//       width: 1,
      
//       curve: 'smooth',
//       colors: ['transparent']
//     },
//     xaxis: {
//       categories:    ['Case', 'Opinion File',],
//     },
//     yaxis: {
//       title: {
//         text: 'Count'
//       }
//     },
//     fill: {
//       opacity: 1
//     },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return  val
//         }
//       }
//     },
//     fill:['#027cd1']
//   })
//   const [hearingList, setHearingList] = useState([]);

//   const [chartOptions2,setchartOptions2] = useState({
//     series: [],

//     chart: {
//       toolbar: {
//         show: false
//       }
//     },
//     plotOptions: {
      
//       bar: {
//         horizontal: false,
//         columnWidth: '55%',
//         endingShape: 'rounded',
//         dataLabels: {
//           position: 'top', // top, center, bottom
//         },
        
//       },
//     },
//     dataLabels: {
//       enabled: false
//     },
//     grid:{
//       show: true,
//       borderColor: '#fafafa',
//     },
  
//     stroke: {
//       // show: true,
//       width: 1,
      
//       curve: 'smooth',
//       colors: ['transparent']
//     },
//     xaxis: {
//       categories: ['Bill Amount', 'Payment Collection'],
//     },
//     yaxis: {
//       title: {
//         text: 'Count'
//       }
//     },
//     fill: {
//       opacity: 1
//     },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return  val
//         }
//       }
//     },
//     fill:['#027cd1']
//   })



//   async function getUpcomingHearings() {
//     try {
//       const res = await GetActivityService("case");
//       const list = res?.data?.datas || [];
  
//       // Today's date
//       const today = new Date();
//       today.setHours(0,0,0,0);
  
//       // Filter only future next hearing dates
//       const upcoming = list.filter(a =>
//         a?.next_hearing_date &&
//         new Date(a.next_hearing_date) >= today
//       );
  
//       // Sort ascending (nearest hearing first)
//       upcoming.sort((a, b) =>
//         new Date(a.next_hearing_date) - new Date(b.next_hearing_date)
//       );
  
//       setHearingList(upcoming);
  
//     } catch (err) {
//       console.log("Error fetching hearings", err);
//     }
//   }
  

//   async function getData(){
//     const response = await GetHomeDataService()

//     let chartOptions = []

//     let d = response?.data?.data

//     let arr = [
//       {
//         name:"Total",
//         data:[d?.total_case,d?.total_opinion_file]
//       },
//       {
//         name:"Completed",
//         data:[d?.total_case_completed,d?.total_opinion_file_completed]
//       },
//       {
//         name:"Pending",
//         data:[d?.total_case_pending,d?.total_opinion_file_pending]
//       },
//       {
//         name:"Progress",
//         data:[d?.total_case_progress,d?.total_opinion_file_progress]
//       },
//       {
//         name:"Hold",
//         data:[d?.total_case_hold,d?.total_opinion_file_completed]
//       },
    
//     ]
//     chartOptions.push('Cases')
//     chartOptions.push('Opinion File')

//     setchartOptions1({
//       ...chartOptions1,
//       series:arr,
//       xaxis:{
//         categories:chartOptions
//       }
//     })

//     setchartOptions2({
//       ...chartOptions2,
//       series:[{
//         name:"Amount",
//         data:[
//           d?.today_bills?.totalAmountWithGst,
//           d?.today_payment?.totalAmountWithGst,
//         ]
//       }],
//     })
//     setdata(response?.data?.data)
//   }

//   let rupeeIndian = Intl.NumberFormat("en-IN", {
//     style: "currency",
//     currency: "INR",
//   });

//   return (
//     <div className='md:h-screen md:min-h-screen md:max-h-screen md:overflow-hidden'>

//       <div className='md:flex'>
//         <DashboardMenu />
//         <div className='md:w-[100%] md:w-[86%] flex md:h-screen md:min-h-screen md:max-h-screen md:overflow-y-scroll'>
//           <div className='w-[100%] px-4 pt-5 '>
           
//            {/* section 1  */}
//            <div className='grid items-center grid-cols-2 border-t border-b border-l border-r md:grid-cols-6 justify-evenly border-slate-100'>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><PiSuitcaseSimple size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> Total Case </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbDeviceAirpodsCase size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> Active Case </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case_pending + data?.total_case_progress))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbBriefcaseOff size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> InActive Case </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case_hold))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlineDriveFileMove size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> Total Opinion File </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><FiFileText size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> Active Opinion File </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file_pending + data?.total_opinion_file_progress))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlineDriveFileMoveRtl size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> InActive Opinion File </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file_hold))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' onClick={()=>navigator('/bills/list')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><RiBillLine size={23} className='p-1 mr-2 text-purple-500 bg-purple-200' /> Total Bills </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.bills))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlinePayment size={23} className='p-1 mr-2 text-purple-500 bg-purple-200' /> Total Payment </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.payment))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><AiOutlineAlert size={23} className='p-1 mr-2 text-red-500 bg-red-200' /> Total Reminder </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.reminder))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><LuCalendarClock size={23} className='p-1 mr-2 text-red-500 bg-red-200' /> Active Reminder </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.reminder_active))?.split('.')[0].slice(1)}</h6>
//                   </div>

//                   <div className='w-full px-2 py-2 border-r border-slate-100'>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbMoneybag size={23} className='p-1 mr-2 text-green-500 bg-green-200' /> Total Revenue </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.billamount?.totalAmountWithGst))?.split('.')[0].slice(0)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbMoneybag size={23} className='p-1 mr-2 text-green-500 bg-green-200' /> Collect Revenue </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.paymentamount?.totalAmountWithGst))?.split('.')[0].slice(0)}</h6>
//                   </div>
//            </div>  

//            {/* section 2  */}
//            <div className='mt-5 md:flex md:mt-2'>
//               <div className=' md:w-[29.5%] md:mr-[0.5%] border border-slate-100 p-5 px-3 py-2'>

//               <h6 className='text-[13px] font-[700]'>Revenue Generated</h6>

//               <div className='flex '>
//                 <div className='-mt-5 -ml-14'>
//                 <ReactApexChart options={chartOptions} size={'10'} series={
//                   [Math.floor((data?.paymentamount?.totalAmountWithGst/data?.billamount?.totalAmountWithGst)*100)]} type="radialBar" width={240} height={240} />
//                 </div>
//                 <div className='w-[100%] -ml-[40px] -mt-8 -ml-2'>

//                 <div className='relative w-full mt-16'>
//                   <h6 className='text-[11px] font-[600]'>Bill Amount </h6>
//                     <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-2 dark:bg-gray-700">
//                       <div className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none w-[100%]`} style={{width:data?.billamount?.totalAmountWithGst > 0 ? '100%' : '0%'}}  >{data?.billamount?.totalAmountWithGst > 0 ? '100%' : '0%'}</div>
//                     </div>
//                     <h6 className='text-[11px] font-[700] absolute top-0 right-0'>{rupeeIndian?.format(JSON.stringify(data?.billamount?.totalAmountWithGst))?.split('.')[0]}</h6>

//                 </div>

//                 <div className='relative w-full mt-4 ml-0'>
//                   <h6 className='text-[11px] font-[600]'>Payment Collected </h6>
//                     <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-4 dark:bg-gray-700">
//                       <div className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none `} style={{width: data?.paymentamount?.totalAmountWithGst  >= data?.billamount?.totalAmountWithGst ? '100%' :  `${Math.round((data?.paymentamount?.totalAmountWithGst/data?.billamount?.totalAmountWithGst))*100}%`}} >{data?.paymentamount?.totalAmountWithGst > 0 ?  Math.round((data?.paymentamount?.totalAmountWithGst / data?.billamount?.totalAmountWithGst)*100): 0}%</div>
//                     </div>
//                     <h6 className='text-[11px] font-[700] absolute top-0 right-0'>{rupeeIndian?.format(JSON.stringify(data?.paymentamount?.totalAmountWithGst))?.split('.')[0]}</h6>

//                 </div>
              
//                 </div>
//               </div>

//               </div>

//               <div className={`border mt-2 md:mt-0 md:w-[69.5%] md:ml-[0.5%]   border-slate-100 px-3 py-2 relative`}>
//                     <h1 className='text-[13px] font-[700]'>Case / Opinion File</h1>
//                     <ReactApexChart options={chartOptions1} size={'10'} series={chartOptions1.series} type="bar" width={'100%'}  height={220} />
//               </div>
//            </div>

//            {/* section 3  */}
//            <div className='mt-5 md:flex md:mt-2'>

//            <div className='border md:w-[33%] border-slate-100 p-2'>
//               <h6 className='text-[13px] font-[700]'>Today Case / Opinion File Register ({data?.today_case_total})</h6>

//                 {data?.today_case_total === 0 &&
//                  <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//                  <IoCalendarClearOutline size={40} color="#ddd" />
//                  <h6 className='text-[11px] mt-2'>No Case / Opinion File found for today</h6>
//                  <h6 className='text-[11px] text-center mt-1'>We found that you have not added any case or opinion file for today session.</h6>
//                  </div>
//                 }
              
//                 {(data?.today_case?.length > 0 || data?.today_office_file?.length > 0) &&
//                 <div className='overflow-y-scroll min-h-[85%] max-h-[85%]'>
//                   <div className='flex text-[11px] border mt-2'>
//                     <h6 className='w-[25%] p-1 border-r'>Client</h6>
//                     <h6 className='w-[25%] p-1 border-r'>Office File No / Case No</h6>
//                     <h6 className='w-[25%] p-1 border-r'>Case Work</h6>
//                     <h6 className='w-[25%] p-1 '>Status</h6>
//                   </div>

//                   {data?.today_case?.map((c,i)=>(
//                     <div key={i} onClick={()=>navigator('/case/civil_court/detail',{state:c})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
//                     <h6 className='w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r'>{c?.client?.name}</h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.office_file_no} / {c?.case_no}</h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.case_details}</h6>
//                     <h6 className='w-[25%] truncate p-1 '>{c?.status}</h6>
//                 </div>
//                   ))}

//                   {data?.today_office_file?.map((c,i)=>(
//                     <div key={i} onClick={()=>navigator('/opinion_files/list/detail',{state:c})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
//                     <h6 className='w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r'>{c?.client?.name}</h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.office_file_no} </h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.work_details}</h6>
//                     <h6 className='w-[25%] truncate p-1 '>{c?.status}</h6>
//                 </div>
//                   ))}
//                 </div>}


//              </div>

//              <div className='border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2'>
//               <h6 className='text-[13px] font-[700]'>Upcoming Reminder ({data?.today_reminder_total})</h6>

//                  {data?.today_reminder_total === 0 &&
//                  <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//                   <IoCalendarClearOutline size={40} color="#ddd" />
//                   <h6 className='text-[11px] mt-2'>No Reminder found for today</h6>
//                   <h6 className='text-[11px] text-center'>We found that you have not added any reminder for today session.</h6>
//                  </div>}

//                {data?.today_reminder_total > 0 &&
//                  <div className='overflow-y-scroll min-h-[85%] max-h-[85%]'>
//                     <div className='flex text-[11px] border mt-2'>
//                         <h6 className='w-[50%] p-1 border-r'>Case Info</h6>
//                         <h6 className='w-[25%] p-1 border-r'>Title</h6>
//                         <h6 className='w-[25%] p-1 '>Summary</h6>
//                       </div>

//                     {data?.today_reminder?.map((r,i)=>(
//                         <div key={i} onClick={()=>r?.type === 'case' ?  navigator(`/case/${r?.case?.case_type?.name == "Civil Court" ? "civil_court" : "high_court"}/detail`,{state:r?.case}) : navigator('/opinion_files/list/detail',{state:r?.opinion_file})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
//                             <h6 className='w-[50%] truncate p-1 font-[700] text-sky-800 border-r'> Type : <span className='underline'>{r?.type}</span> 
//                             {r?.type === 'opinion_file' && 
//                               <div className='break-all'>
//                                 <span>Opinion Info : {r?.opinion_file?.office_file_no} / {r?.opinion_file?.work_details}  <br></br> 
//                                 Client Info : {r?.opinion_file?.client?.name}  <br></br>  
//                                 {r?.opinion_file?.client?.mobile}
//                                 </span>
//                               </div>
//                             }

//                             {r?.type === 'case' && 
//                               <div className='break-all'>
//                                 <span>Case  Info : {r?.case?.office_file_no} / {r?.case?.case_details}  <br></br> 
//                                 Client Info : {r?.case?.client?.name}  <br></br>  
//                                 {r?.case?.client?.mobile}
//                                 </span>
//                               </div>
//                             }
//                             </h6>
//                             <h6 className='w-[25%] truncate p-1 font-[700] border-r'>{r?.title}</h6>
//                             <h6 className='w-[25%] truncate p-1 '>{r?.description} </h6>
//                         </div>
//                     ))}
//                  </div>}
//              </div>

//              <div className='border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2'>
//               <h6 className='text-[13px] font-[700]'>Today Bill Raised vs Collection</h6>

//                   {(data?.today_bills?.totalAmountWithGst === 0 && data?.today_payment?.totalAmountWithGst === 0) && 
//                   <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//                       <IoCalendarClearOutline size={40} color="#ddd" />
//                       <h6 className='text-[11px] mt-2'>No Bill or Payment Collection Made</h6>
//                       <h6 className='text-[11px] text-center mt-1'>We found that you have not created any bill or payment collection made for today.</h6>
//                   </div>}

//                   {(data?.today_bills?.totalAmountWithGst > 0 || data?.today_payment?.totalAmountWithGst > 0) &&
//                   <div className=''>
//                     <ReactApexChart options={chartOptions2} size={'10'} series={chartOptions2?.series} type="bar" width={'100%'}  height={150} />
//                   </div>}

//              </div>

//              <div className='border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2'>
//   <h6 className='text-[13px] font-[700]'>
//     Upcoming Hearing ({hearingList?.length})
//   </h6>

//   {hearingList?.length === 0 &&
//     <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//       <IoCalendarClearOutline size={40} color="#ddd" />
//       <h6 className='text-[11px] mt-2'>No upcoming hearing</h6>
//       <h6 className='text-[11px] text-center'>No next hearing dates found.</h6>
//     </div>
//   }

//   {hearingList?.length > 0 &&
//     <div className='overflow-y-scroll min-h-[85%] max-h-[85%]'>
//       <div className='flex text-[11px] border mt-2'>
//         <h6 className='w-[40%] p-1 border-r'>Case</h6>
//         <h6 className='w-[30%] p-1 border-r'>Date</h6>
//         <h6 className='w-[30%] p-1'>Stage</h6>
//       </div>

//       {hearingList.map((h, i) => (
//         <div key={i}
//           onClick={() =>
//             navigator(
//               `/case/${h?.case?.case_type?.name == "Civil Court" ? "civil_court" : "high_court"}/detail`,
//               { state: h?.case }
//             )
//           }
//           className='flex cursor-pointer text-[11px] border-l border-r border-b'
//         >
//           <h6 className='w-[40%] truncate p-1 font-[700] underline text-sky-800 border-r'>
//             {h?.client?.name}
//           </h6>
//           <h6 className='w-[30%] truncate p-1 border-r'>
//             {moment(h?.next_hearing_date).format("LL")}
//           </h6>
//           <h6 className='w-[30%] truncate p-1'>
//             {h?.stage?.name || "---"}
//           </h6>
//         </div>
//       ))}
//     </div>
//   }
// </div>

             
//            </div>

//           </div>
//         </div> 

        
//       </div>
//     </div>
    
//   )
// }

// export default Dashboard








//===============>> Old one kp code





// import React,{useState,useEffect} from 'react'
// import {RiCustomerService2Line} from 'react-icons/ri';
// import DashboardMenu from './DashboardMenu'
// // import { GetTodayLeadReminderService, getInsentiveTargetAchieved, getTargetAchieved } from '../../services/LeadServices';
// import { BiLandscape } from 'react-icons/bi';
// import { useNavigate } from 'react-router-dom';
// import { GetHomeDataService } from '../../services/HomeDataServices';

// import { PiSuitcaseSimple } from "react-icons/pi";
// import { TbBriefcaseOff } from "react-icons/tb";
// import { TbDeviceAirpodsCase } from "react-icons/tb";
// import { MdOutlineDriveFileMove,MdOutlineDriveFileMoveRtl,MdOutlinePayment } from "react-icons/md";
// import { FiFileText } from "react-icons/fi";
// import { RiBillLine } from "react-icons/ri";
// import { LuCalendarClock } from "react-icons/lu";
// import { AiOutlineAlert } from "react-icons/ai";
// import { TbMoneybag } from "react-icons/tb";
// import ReactApexChart from 'react-apexcharts';
// import { IoCalendarClearOutline } from "react-icons/io5";

// function Dashboard() {


//   const navigator = useNavigate()
//   const [data,setdata] = useState({
//       total_case:0,
//       total_case_pending:0,
//       total_case_progress:0,
//       total_case_completed:0,
//       total_case_hold:0,

//       total_opinion_file:0,
//       total_opinion_file_pending:0,
//       total_opinion_file_progress:0,
//       total_opinion_file_completed:0,
//       total_opinion_file_hold:0,

//       bills:[],
//       billamount:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       payment:[],
//       paymentamount:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       reminder:[],
//       reminder_active:[],
//       today_case:[],
//       today_office_file:[],
//       today_case_total:0,
//       today_bills:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       today_payment:{totalAmountWithGst:0,totalAmountWithoutGst:0},
//       today_reminder:[],
//       today_reminder_total:0

//   })
  
//   useEffect(()=>{
//     getData()
//   },[])

//   const chartOptions = {
//     plotOptions: {
//       color:['#000','#000'],
//       size:'40%',
//       radialBar: {
//           inverseOrder: false,
//           startAngle: 0,
//           endAngle: 360,
//           offsetX: 0,
//           offsetY: 0,
//           hollow: {
//               margin: 5,
//               size: '50%',
//               color: '#f2f2f2',
//               image: undefined,
//               imageWidth: 150,
//               imageHeight: 150,
//               imageOffsetX: 0,
//               imageOffsetY: 0,
//               imageClipped: true,
//               position: 'front',
             
//           },
//           track: {
//               show: true,
//               startAngle: undefined,
//               endAngle: undefined,
//               background: '#f2f2f2',
//               strokeWidth: '50%',
//               opacity: 1,
//               margin: 5, 
//               dropShadow: {
//                   enabled: false,
//                   top: 0,
//                   left: 0,
//                   opacity: 1
//               }
//           },
//           dataLabels: {
//               show: true,
//               name: {
//                   show: true,
//                   fontSize: '12px',
//                   fontFamily: undefined,
//                   fontWeight: 400,
//                   color: undefined,
//                   color:'#000',
//                 },
//                 value: {
//                   show: true,
//                   fontSize: '14px',
//                   fontFamily: undefined,
//                   fontWeight: 900,
//                   color: undefined,
//                   offsetY: 1,
//                   formatter: function (val) {
//                     return val + '%'
//                   }
//                 },
                
//           },
          
//       },
//     },
//     labels: ['Revenue Forecast'],
//     fill:{
//       colors: ['#008ef2'],
//     }
//   }

//   const [chartOptions1,setchartOptions1] = useState({
//     series: [],

//     chart: {
//       toolbar: {
//         show: false
//       }
//     },
//     plotOptions: {
      
//       bar: {
//         horizontal: false,
//         columnWidth: '55%',
//         endingShape: 'rounded',
//         dataLabels: {
//           position: 'top', // top, center, bottom
//         },
        
//       },
//     },
//     dataLabels: {
//       enabled: false
//     },
//     grid:{
//       show: true,
//       borderColor: '#fafafa',
//     },
  
//     stroke: {
//       // show: true,
//       width: 1,
      
//       curve: 'smooth',
//       colors: ['transparent']
//     },
//     xaxis: {
//       categories:    ['Case', 'Opinion File',],
//     },
//     yaxis: {
//       title: {
//         text: 'Count'
//       }
//     },
//     fill: {
//       opacity: 1
//     },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return  val
//         }
//       }
//     },
//     fill:['#027cd1']
//   })

//   const [chartOptions2,setchartOptions2] = useState({
//     series: [],

//     chart: {
//       toolbar: {
//         show: false
//       }
//     },
//     plotOptions: {
      
//       bar: {
//         horizontal: false,
//         columnWidth: '55%',
//         endingShape: 'rounded',
//         dataLabels: {
//           position: 'top', // top, center, bottom
//         },
        
//       },
//     },
//     dataLabels: {
//       enabled: false
//     },
//     grid:{
//       show: true,
//       borderColor: '#fafafa',
//     },
  
//     stroke: {
//       // show: true,
//       width: 1,
      
//       curve: 'smooth',
//       colors: ['transparent']
//     },
//     xaxis: {
//       categories: ['Bill Amount', 'Payment Collection'],
//     },
//     yaxis: {
//       title: {
//         text: 'Count'
//       }
//     },
//     fill: {
//       opacity: 1
//     },
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return  val
//         }
//       }
//     },
//     fill:['#027cd1']
//   })

//   async function getData(){
//     const response = await GetHomeDataService()

//     let chartOptions = []

//     let d = response?.data?.data

//     let arr = [
//       {
//         name:"Total",
//         data:[d?.total_case,d?.total_opinion_file]
//       },
//       {
//         name:"Completed",
//         data:[d?.total_case_completed,d?.total_opinion_file_completed]
//       },
//       {
//         name:"Pending",
//         data:[d?.total_case_pending,d?.total_opinion_file_pending]
//       },
//       {
//         name:"Progress",
//         data:[d?.total_case_progress,d?.total_opinion_file_progress]
//       },
//       {
//         name:"Hold",
//         data:[d?.total_case_hold,d?.total_opinion_file_completed]
//       },
    
//     ]
//     chartOptions.push('Cases')
//     chartOptions.push('Opinion File')

//     setchartOptions1({
//       ...chartOptions1,
//       series:arr,
//       xaxis:{
//         categories:chartOptions
//       }
//     })

//     setchartOptions2({
//       ...chartOptions2,
//       series:[{
//         name:"Amount",
//         data:[
//           d?.today_bills?.totalAmountWithGst,
//           d?.today_payment?.totalAmountWithGst,
//         ]
//       }],
//     })
//     setdata(response?.data?.data)
//   }

//   let rupeeIndian = Intl.NumberFormat("en-IN", {
//     style: "currency",
//     currency: "INR",
//   });

//   return (
//     <div className='md:h-screen md:min-h-screen md:max-h-screen md:overflow-hidden'>

//       <div className='md:flex'>
//         <DashboardMenu />
//         <div className='md:w-[100%] md:w-[86%] flex md:h-screen md:min-h-screen md:max-h-screen md:overflow-y-scroll'>
//           <div className='w-[100%] px-4 pt-5 '>
           
//            {/* section 1  */}
//            <div className='grid items-center grid-cols-2 border-t border-b border-l border-r md:grid-cols-6 justify-evenly border-slate-100'>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><PiSuitcaseSimple size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> Total Case </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbDeviceAirpodsCase size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> Active Case </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case_pending + data?.total_case_progress))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/case')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbBriefcaseOff size={23} className='p-1 mr-2 text-blue-500 bg-blue-200' /> InActive Case </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_case_hold))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlineDriveFileMove size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> Total Opinion File </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><FiFileText size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> Active Opinion File </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file_pending + data?.total_opinion_file_progress))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-b border-r border-slate-100' onClick={()=>navigator('/opinion_files')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlineDriveFileMoveRtl size={23} className='p-1 mr-2 text-yellow-500 bg-yellow-200' /> InActive Opinion File </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.total_opinion_file_hold))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' onClick={()=>navigator('/bills/list')}>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><RiBillLine size={23} className='p-1 mr-2 text-purple-500 bg-purple-200' /> Total Bills </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.bills))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><MdOutlinePayment size={23} className='p-1 mr-2 text-purple-500 bg-purple-200' /> Total Payment </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.payment))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><AiOutlineAlert size={23} className='p-1 mr-2 text-red-500 bg-red-200' /> Total Reminder </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.reminder))?.split('.')[0].slice(1)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><LuCalendarClock size={23} className='p-1 mr-2 text-red-500 bg-red-200' /> Active Reminder </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.reminder_active))?.split('.')[0].slice(1)}</h6>
//                   </div>

//                   <div className='w-full px-2 py-2 border-r border-slate-100'>
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbMoneybag size={23} className='p-1 mr-2 text-green-500 bg-green-200' /> Total Revenue </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.billamount?.totalAmountWithGst))?.split('.')[0].slice(0)}</h6>
//                   </div>
//                   <div className='w-full px-2 py-2 border-r border-slate-100' >
//                     <h6 className='text-[12px] text-slate-700 font-[600] flex items-center'><TbMoneybag size={23} className='p-1 mr-2 text-green-500 bg-green-200' /> Collect Revenue </h6>
//                     <h6 className='text-[14px] mt-1'>{rupeeIndian?.format(JSON.stringify(data?.paymentamount?.totalAmountWithGst))?.split('.')[0].slice(0)}</h6>
//                   </div>
//            </div>  

//            {/* section 2  */}
//            <div className='mt-5 md:flex md:mt-2'>
//               <div className=' md:w-[29.5%] md:mr-[0.5%] border border-slate-100 p-5 px-3 py-2'>

//               <h6 className='text-[13px] font-[700]'>Revenue Generated</h6>

//               <div className='flex '>
//                 <div className='-mt-5 -ml-14'>
//                 <ReactApexChart options={chartOptions} size={'10'} series={
//                   [Math.floor((data?.paymentamount?.totalAmountWithGst/data?.billamount?.totalAmountWithGst)*100)]} type="radialBar" width={240} height={240} />
//                 </div>
//                 <div className='w-[100%] -ml-[40px] -mt-8 -ml-2'>

//                 <div className='relative w-full mt-16'>
//                   <h6 className='text-[11px] font-[600]'>Bill Amount </h6>
//                     <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-2 dark:bg-gray-700">
//                       <div className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none w-[100%]`} style={{width:data?.billamount?.totalAmountWithGst > 0 ? '100%' : '0%'}}  >{data?.billamount?.totalAmountWithGst > 0 ? '100%' : '0%'}</div>
//                     </div>
//                     <h6 className='text-[11px] font-[700] absolute top-0 right-0'>{rupeeIndian?.format(JSON.stringify(data?.billamount?.totalAmountWithGst))?.split('.')[0]}</h6>

//                 </div>

//                 <div className='relative w-full mt-4 ml-0'>
//                   <h6 className='text-[11px] font-[600]'>Payment Collected </h6>
//                     <div className="w-full bg-gray-200 mt-1 rounded-full h-2.5 mb-4 dark:bg-gray-700">
//                       <div className={`bg-gray-700  rounded-full h-2.5 flex items-center justify-center dark:bg-gray-300 font-[900  text-[8px] text-white text-center leading-none `} style={{width: data?.paymentamount?.totalAmountWithGst  >= data?.billamount?.totalAmountWithGst ? '100%' :  `${Math.round((data?.paymentamount?.totalAmountWithGst/data?.billamount?.totalAmountWithGst))*100}%`}} >{data?.paymentamount?.totalAmountWithGst > 0 ?  Math.round((data?.paymentamount?.totalAmountWithGst / data?.billamount?.totalAmountWithGst)*100): 0}%</div>
//                     </div>
//                     <h6 className='text-[11px] font-[700] absolute top-0 right-0'>{rupeeIndian?.format(JSON.stringify(data?.paymentamount?.totalAmountWithGst))?.split('.')[0]}</h6>

//                 </div>
              
//                 </div>
//               </div>

//               </div>

//               <div className={`border mt-2 md:mt-0 md:w-[69.5%] md:ml-[0.5%]   border-slate-100 px-3 py-2 relative`}>
//                     <h1 className='text-[13px] font-[700]'>Case / Opinion File</h1>
//                     <ReactApexChart options={chartOptions1} size={'10'} series={chartOptions1.series} type="bar" width={'100%'}  height={220} />
//               </div>
//            </div>

//            {/* section 3  */}
//            <div className='mt-5 md:flex md:mt-2'>

//            <div className='border md:w-[33%] border-slate-100 p-2'>
//               <h6 className='text-[13px] font-[700]'>Today Case / Opinion File Register ({data?.today_case_total})</h6>

//                 {data?.today_case_total === 0 &&
//                  <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//                  <IoCalendarClearOutline size={40} color="#ddd" />
//                  <h6 className='text-[11px] mt-2'>No Case / Opinion File found for today</h6>
//                  <h6 className='text-[11px] text-center mt-1'>We found that you have not added any case or opinion file for today session.</h6>
//                  </div>
//                 }
              
//                 {(data?.today_case?.length > 0 || data?.today_office_file?.length > 0) &&
//                 <div className='overflow-y-scroll min-h-[85%] max-h-[85%]'>
//                   <div className='flex text-[11px] border mt-2'>
//                     <h6 className='w-[25%] p-1 border-r'>Client</h6>
//                     <h6 className='w-[25%] p-1 border-r'>Office File No / Case No</h6>
//                     <h6 className='w-[25%] p-1 border-r'>Case Work</h6>
//                     <h6 className='w-[25%] p-1 '>Status</h6>
//                   </div>

//                   {data?.today_case?.map((c,i)=>(
//                     <div key={i} onClick={()=>navigator('/case/civil_court/detail',{state:c})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
//                     <h6 className='w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r'>{c?.client?.name}</h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.office_file_no} / {c?.case_no}</h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.case_details}</h6>
//                     <h6 className='w-[25%] truncate p-1 '>{c?.status}</h6>
//                 </div>
//                   ))}

//                   {data?.today_office_file?.map((c,i)=>(
//                     <div key={i} onClick={()=>navigator('/opinion_files/list/detail',{state:c})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
//                     <h6 className='w-[25%] truncate p-1 font-[700] underline text-sky-800 border-r'>{c?.client?.name}</h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.office_file_no} </h6>
//                     <h6 className='w-[25%] truncate p-1 border-r'>{c?.work_details}</h6>
//                     <h6 className='w-[25%] truncate p-1 '>{c?.status}</h6>
//                 </div>
//                   ))}
//                 </div>}


//              </div>

//              <div className='border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2'>
//               <h6 className='text-[13px] font-[700]'>Upcoming Reminder ({data?.today_reminder_total})</h6>

//                  {data?.today_reminder_total === 0 &&
//                  <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//                   <IoCalendarClearOutline size={40} color="#ddd" />
//                   <h6 className='text-[11px] mt-2'>No Reminder found for today</h6>
//                   <h6 className='text-[11px] text-center'>We found that you have not added any reminder for today session.</h6>
//                  </div>}

//                {data?.today_reminder_total > 0 &&
//                  <div className='overflow-y-scroll min-h-[85%] max-h-[85%]'>
//                     <div className='flex text-[11px] border mt-2'>
//                         <h6 className='w-[50%] p-1 border-r'>Case Info</h6>
//                         <h6 className='w-[25%] p-1 border-r'>Title</h6>
//                         <h6 className='w-[25%] p-1 '>Summary</h6>
//                       </div>

//                     {data?.today_reminder?.map((r,i)=>(
//                         <div key={i} onClick={()=>r?.type === 'case' ?  navigator(`/case/${r?.case?.case_type?.name == "Civil Court" ? "civil_court" : "high_court"}/detail`,{state:r?.case}) : navigator('/opinion_files/list/detail',{state:r?.opinion_file})} className='flex cursor-pointer text-[11px] border-l border-r border-b'>
//                             <h6 className='w-[50%] truncate p-1 font-[700] text-sky-800 border-r'> Type : <span className='underline'>{r?.type}</span> 
//                             {r?.type === 'opinion_file' && 
//                               <div className='break-all'>
//                                 <span>Opinion Info : {r?.opinion_file?.office_file_no} / {r?.opinion_file?.work_details}  <br></br> 
//                                 Client Info : {r?.opinion_file?.client?.name}  <br></br>  
//                                 {r?.opinion_file?.client?.mobile}
//                                 </span>
//                               </div>
//                             }

//                             {r?.type === 'case' && 
//                               <div className='break-all'>
//                                 <span>Case  Info : {r?.case?.office_file_no} / {r?.case?.case_details}  <br></br> 
//                                 Client Info : {r?.case?.client?.name}  <br></br>  
//                                 {r?.case?.client?.mobile}
//                                 </span>
//                               </div>
//                             }
//                             </h6>
//                             <h6 className='w-[25%] truncate p-1 font-[700] border-r'>{r?.title}</h6>
//                             <h6 className='w-[25%] truncate p-1 '>{r?.description} </h6>
//                         </div>
//                     ))}
//                  </div>}
//              </div>

//              <div className='border mt-2 md:mt-0 md:w-[33%] md:ml-[12px] border-slate-100 p-2'>
//               <h6 className='text-[13px] font-[700]'>Today Bill Raised vs Collection</h6>

//                   {(data?.today_bills?.totalAmountWithGst === 0 && data?.today_payment?.totalAmountWithGst === 0) && 
//                   <div className='flex flex-col my-8 w-[80%] mx-auto items-center'>
//                       <IoCalendarClearOutline size={40} color="#ddd" />
//                       <h6 className='text-[11px] mt-2'>No Bill or Payment Collection Made</h6>
//                       <h6 className='text-[11px] text-center mt-1'>We found that you have not created any bill or payment collection made for today.</h6>
//                   </div>}

//                   {(data?.today_bills?.totalAmountWithGst > 0 || data?.today_payment?.totalAmountWithGst > 0) &&
//                   <div className=''>
//                     <ReactApexChart options={chartOptions2} size={'10'} series={chartOptions2?.series} type="bar" width={'100%'}  height={150} />
//                   </div>}

//              </div>


             
//            </div>

//           </div>
//         </div> 

        
//       </div>
//     </div>
    
//   )
// }

// export default Dashboard